import {
  categoryMap,
  accentMatName,
  canvasMatName,
  defaultAccentMatReveal,
  uploadedArtworkMatOverlap,
  floatMountMatName,
  floatMountMatReveal,
  jerseyMatName,
  noMatName,
  heartstagramMatName,
  acrylicMatName,
  whiteMatName,
  stripedMats,
  PHYSICAL_CONVEYANCES,
  DIGITAL_CONVEYANCES,
  matLimits,
  mountLimits,
  matRevealIncrement,
  finalFrameSizeIncrement,
  sizeGroups,
  minimumPixelsPerInchForPrinting,
} from "./hardCodedValues";
import {
  IN_STORE_PICKUP_SIZE_GROUPS,
  OVERSIZE_DIMENSIONS,
  SHIP_TO_HOME_RESTRICTION_DIMENSIONS,
  SHIP_TO_HOME_RESTRICTED_SIZE_GROUPS
} from "./constants";
import { determineSizeGroup } from "./framingUtilities";
import { isMatMultiOpening } from "./multiArtworkFramingUtility";
import { populateFromUrlParams } from "./urlFrameSpec/reader";
import { proportionalArtworkDimensions } from "./proportionalArtworkDimensions.js";
import { snackCaseToCamelCase } from "./objectConverter";
import { getEditingLineItemNumber } from "./editingLineItem";
import ArtworkEditor from "./ArtworkEditor";
import axios from "axios";
import frameSpecValidators from "./validators/frameSpecValidators";
import JoineryClient from "./apihelpers/glazeClient";
import hardCodedFrameSpecGenerator from "./hardCodedFrameSpecGenerator";
import urlFrameSpecMap from "./urlFrameSpec/map";
import urlFrameSpecWriter from "./urlFrameSpec/writer";
import presetMatWidths from "./presetMatWidths";
import { associateInstructions as associateInstructionDefinitions } from "../veneer-vite/src/assets/javascripts/associateInstructions.js";

import { uniqBy, cloneDeep, omit, omitBy, isEmpty, pick, over } from "lodash";
import { sortLineItemProperties } from "./sortLineItemProperties";
import { encode, decode } from "base-64";

const cartUrlFallback = "https://framebridge-cart.netlify.app";

const minimumPPI = minimumPixelsPerInchForPrinting;
const _mouldingsCache = {};

const _surchargeProductDetailsCache = {};

function fetchSurchargeProductDetails (host, productId) {
  const key = productId;

  if (!_surchargeProductDetailsCache[key]) {
    _surchargeProductDetailsCache[key] = axios
      .get(`${host}/api/v1/shopify_product_details/${productId}`)
      .then(resp => resp.data || null)
      .catch(error => {
        delete _surchargeProductDetailsCache[key];
        throw error;
      });
  }

  return _surchargeProductDetailsCache[key];
}

export default class FrameSpec {
  constructor (frameSpecData, joineryToken, joineryHost, shopifyHost, internalCall, inBundle) {
    if (!internalCall) {
      throw new Error("You must use the `build` constructor");
    }

    if (!frameSpecData) {
      throw new Error("Missing required build parameter frameSpecData");
    }

    if (shopifyHost && !joineryHost) {
      throw new Error("Missing required build parameter joineryHost when in Shopify Mode");
    }

    Object.assign(this, frameSpecData);
    this.joineryHost = joineryHost;
    this.joineryClient = new JoineryClient(joineryToken, { rejectOnNotOk: true, baseUrl: joineryHost });
    this.joineryToken = joineryToken;
    this.errors = {};
    this.shopifyHost = shopifyHost || (
      window && window.Shopify && window.Shopify.routes.root
    );
    this.inBundle = inBundle || false;
  }

  static async build ({
    frameSpecData,
    number,
    authorizationToken,
    orderToken,
    token,
    joineryHost,
    query = {},
    productSlug,
    templateOptions,
    artworksVersion,
    shopifyHost,
    artwork,
    snapshot,
    usedFullMats,
    allFullMats,
    allFullMouldings,
    inBundle
  }) {
    let localFrameSpecData;
    const asyncData = [];

    if (frameSpecData) {
      localFrameSpecData = frameSpecData;
    } else if (number) {
      try {
        const authHeaders = authorizationToken ? { "Framebridge-Resource-Authorization-Token": authorizationToken } :
                            orderToken ? { "X-Spree-Order-Token": orderToken } :
                            {};
        const additionalParams = artworksVersion ? `?artworks_version=${artworksVersion}` : "";
        const result = await axios.get(`${joineryHost}/api/v1/framing_specifications/${number}${additionalParams}`, { headers: authHeaders });
        if (result.data) {
          localFrameSpecData = result.data;
        } else {
          throw ({data: {error: "No data for this frameSpec"}});
        }
      } catch (error) {
        throw new Error(error);
      }
    } else {
      localFrameSpecData = populateFromUrlParams(query);

      if (Object.keys(localFrameSpecData).length === 0) {
        const mouldingPermalink = query["moulding"];
        const matSku = query["mat"];

        if (mouldingPermalink || matSku) {
          localFrameSpecData = hardCodedFrameSpecGenerator({
            mouldingPermalink,
            matSku,
            withArtwork: templateOptions?.withArtwork || true
          });
        } else {
          localFrameSpecData = hardCodedFrameSpecGenerator(templateOptions || { withArtwork: false });
        }
      }  
    }

    // if an orderToken is provided, store it in the framespec for further use
    if (orderToken) {
      localFrameSpecData.orderToken = orderToken;
    }

    // if an authorizationToken was provided, store it in the framespec for further use
    if (authorizationToken) {
      localFrameSpecData.authorizationToken = authorizationToken;
    }

    // If we are provided an explicit product/variant then override the frameSpec with those
    localFrameSpecData.catalog = localFrameSpecData.catalog || {};
    localFrameSpecData.catalog.shopifyProductId = query["product"] || localFrameSpecData.catalog.shopifyProductId;
    localFrameSpecData.catalog.shopifyVariantId = query["variant"] || localFrameSpecData.catalog.shopifyVariantId;

    if (artwork) {
      localFrameSpecData.artworks = [artwork];
    }

    if (localFrameSpecData.artworks && localFrameSpecData.artworks[0] && !localFrameSpecData.artworks[0].offset) {
      localFrameSpecData.artworks[0].offset = {
        topInInches: "auto",
        leftInInches: "auto"
      }
    }

    if (snapshot) {
      localFrameSpecData.snapshotMode = true;
    }

    const frameSpec = new FrameSpec(localFrameSpecData, token, joineryHost, shopifyHost, true, inBundle);

    if (frameSpec.catalog.shopifyProductId) {
      asyncData.push(frameSpec.loadShopifyProduct());
    }

    if (usedFullMats) {
      frameSpec._usedFullMats = usedFullMats;
    }

    if (allFullMats) {
      frameSpec._allFullMats = allFullMats;
    }

    if (allFullMouldings) {
      frameSpec._allMouldingOptions = allFullMouldings;
    }

    if (!usedFullMats && !allFullMats) {
      asyncData.push(frameSpec.getUsedFullMats());
    }

    await Promise.all(asyncData);

    frameSpec.setArtworkOpeningDimensions();

    frameSpec.setupNoAcrylic();

    return frameSpec;
  }

  static parseMats (matOptions) {
    return matOptions.map((mat) => {
      const isStriped = mat.surfaceTextureUrl && mat.surfaceTextureUrl.includes('Stripe');

      return {
        ...mat,
        style: isStriped ? "striped" : mat.style,
        surfaceTextureThumbnailUrl: mat.surfaceTextureThumbnailUrl || mat.surfaceTextureUrl,
        openings: mat.openings && mat.openings.length ? mat.openings : undefined,
      };
    });
  }

  static async getMatsByNames (names, joineryHost) {
    try {
      const mats = await axios.get(`${joineryHost}/api/v1/mats?names=${names.join(',')}`);
      return FrameSpec.parseMats(mats.data.map((mat) => snackCaseToCamelCase(mat)));
    } catch (error) {
      console.error("Error from getMats");
      console.error(error);
    }

    return [];
  }

  static getMatStyles (mat) {
    if (!mat?.style) {
      return [];
    }

    return mat.style.replace(/\s+/g, '').split(',');
  }
  static getMatPrimaryStyle (mat) {
    const matStyles = FrameSpec.getMatStyles(mat);
    return matStyles[0];
  }
  static isMatInStyle (mat, style) {
    const matStyles = FrameSpec.getMatStyles(mat);
    if (Array.isArray(style)) {
      return style.some((s) => matStyles.includes(s));
    }
    return matStyles.includes(style);
  }

  static getMouldingTags (moulding) {
    if (!moulding?.tags) {
      return [];
    }

    return moulding.tags.split(',').map((t) => t.trim()).filter((t) => t.length);
  }
  static getMouldingPrimaryTag (moulding) {
    const mouldingTags = FrameSpec.getMouldingTags(moulding);
    return mouldingTags[0];
  }
  static isMouldingInTag (moulding, tag) {
    const mouldingTags = FrameSpec.getMouldingTags(moulding);
    if (Array.isArray(tag)) {
      return tag.some((t) => mouldingTags.includes(t));
    }
    return mouldingTags.includes(tag);
  }

  getFullMat (name) {
    const mats = [...(this._usedFullMats || []), ...(this._allFullMats || [])];
    return mats.find((mat) => mat.name === name);
  }

  getFullPrimaryMat () {
    return this.getFullMat(this.mats.length ? this.mats[0].name : this.mount.name);
  }

  getFullBaseMat () {
    return this.getFullMat(this.mount.name);
  }

  async getUsedFullMats () {
    if ((this._usedFullMats && this._usedFullMats.length) || !this.mats) {
      return;
    }

    const usedMatNames = this.mats.map((mat) => mat.name);
    this._usedFullMats = await FrameSpec.getMatsByNames(usedMatNames, this.joineryHost);
  }

  async getAllFullMats () {
    if (this._allFullMats && this._allFullMats.length) {
      return;
    }

    this._allFullMats = await FrameSpec.getMatsByNames([], this.joineryHost);
  }
  
  setupNoAcrylic () {
    if (this.isCanvasOrCanvasPaper()) {
      this.setNoAcrylic();
    }

    if (this.acrylicRequired === false && this.noAcrylicDisabled()) {
      this.removeNoAcrylic();
    }
  }

  hasShopifyProductAndVariant () {
    return Boolean(this.catalog && this.catalog.shopifyProductId && this.catalog.shopifyVariantId);
  }

  async loadShopifyProduct() {
    this.product = await this.retrieveProductById(this.catalog.shopifyProductId);

    // if it's a post purchase dc product, switch to load the framespec moulding product for snapshot.
    // ideally we will remove the snapshot mode, but currently DC still displays the product/pricing for that product
    if (this.isDesignersChoiceProduct() && this.isPostPurchase() && this.snapshotMode) {
      this.product = null;
      return this.setProductFromMouldingShopify();
    } else if (this.isDesignersChoiceProduct() && !this.isPostPurchase() && !this.designersChoiceRequested) {
      // this is the case when the framespec gets built after adding to cart but before order placed/line item created
      this.designersChoiceRequested = true;
    }
    let defaultVariant = this.product.variants[0];

    if (this.catalog.shopifyVariantId) {
      const onLoadDefault = this.product.variants.find((variant) => {
        return variant.shopifyVariantId === this.catalog.shopifyVariantId;
      });

      if (onLoadDefault) {
        defaultVariant = onLoadDefault;
      }
    }

    if (!this.hasArtwork() && !this.isCustomProduct()) {
      const shopifyCatalog = this.catalog;
      const productCategorySubtype = this.artworks && this.artworks[0] && this.artworks[0].productCategorySubtype

      Object.assign(this, cloneDeep(defaultVariant.frameSpec));
      delete this.id;
      delete this.number;
      delete this.giftBox;
      delete this.templateIdentifier;

      this.catalog = shopifyCatalog;
      if (productCategorySubtype) {
        // when setting subtype before uploading art, we don't want to lose it when fetching the variant spec, so we slice it back in here)
        this.setProductCategorySubtype(productCategorySubtype);
      }
    } else if (this.hasArtwork() && !this.isCustomProduct() && !this.isDesignersChoiceProduct()) {
      const catalogVariant = this.product.variants.find(variant => {
        return variant.shopifyVariantId === this.catalog.shopifyVariantId &&
              variant.frameSpec &&
              variant.frameSpec.artworks &&
              variant.frameSpec.artworks[0] &&
              variant.frameSpec.artworks[0].exterior;
      });
      // I think this is where I should prevent the art size manipulation


      // if a square is selected, don't try to match on artwork orientation, keep the square
      // else, look for a variant with the same orientation as the uploaded artwork
      if (catalogVariant && catalogVariant.frameSpec.artworks[0].exterior.widthInInches !== catalogVariant.frameSpec.artworks[0].exterior.heightInInches) {
        const orientation = this.getOrientation();
        const sameFrameStyleVariants = this.getSameFrameStyleVariants();

        if (sameFrameStyleVariants.length) {
          let orientedVariants = sameFrameStyleVariants.filter((variant) => {
            if (!variant.frameSpec.artworks[0] || !variant.frameSpec.artworks[0].exterior) {
              return false;
            }

            const width = variant.frameSpec.artworks[0].exterior.widthInInches;
            const height = variant.frameSpec.artworks[0].exterior.heightInInches;

            if (orientation === 'landscape') {
              return width > height;
            } else if (orientation === 'portrait') {
              return height > width;
            } else if (orientation === 'square') {
              return width === height;
            }
            return false;
          });

          if (orientedVariants.length) {
            defaultVariant = orientedVariants.find((variant) => {
              // Check if the catalog variant is in matching orientation options
              if (variant.shopifyVariantId === this.catalog.shopifyVariantId) {
                return true;
              }
              // Otherwise check if a matching orientation option has the same overall size as the catalog variant
              return variant.frameSpec.artworks[0].exterior.widthInInches === catalogVariant.frameSpec.artworks[0].exterior.heightInInches &&
                    variant.frameSpec.artworks[0].exterior.heightInInches === catalogVariant.frameSpec.artworks[0].exterior.widthInInches;
            }) || orientedVariants[0];
          }
        }
      }

      const shopifyCatalog = this.catalog;
      const variantSpecExterior = defaultVariant.frameSpec.artworks[0].exterior;
      const variantSpec = cloneDeep(defaultVariant.frameSpec);
      delete variantSpec.giftBox;
      delete variantSpec.artworks;
      delete variantSpec.previewImage;
      delete variantSpec.pricing;
      delete variantSpec.id;
      delete variantSpec.number;
      delete variantSpec.templateIdentifier;
      delete variantSpec.storyPocket;

      // Store the possibly-customized mat caption and mouldingPlate
      const matCaption = this.mats[0] ? this.mats[0].matCaption : false;
      const mouldingPlate = this.moulding.mouldingPlate ? this.moulding.mouldingPlate : false;
      const matPlaque = this.matPlaque ? this.matPlaque : false;
      const mouldingEngraving = this.moulding.mouldingEngraving || false;
      const associateInstructions = this.associateInstructions ? this.associateInstructions : false;

      // Store existing mount if it has been set to prevent reset to default
      const mount = this.mount ? cloneDeep(this.mount) : false;

      Object.assign(this, variantSpec);

      // Add personalization back in
      if (matCaption) {
        this.setMatCaption(matCaption);
      }

      if (mouldingPlate) {
        this.setMouldingPlate(mouldingPlate);
      }

      if (matPlaque) {
        this.setMatPlaque(matPlaque);
      }
      
      if (mouldingEngraving) {
        this.setMouldingEngraving(mouldingEngraving);
      }

      if (mount) {
        this.mount = mount;
      }

      if (associateInstructions) {
        this.associateInstructions = associateInstructions;
      }

      this.artworks[0].exterior = variantSpecExterior;
      this.catalog = shopifyCatalog;
    }

    if (this.isCustomProduct() && !this.isPostPurchaseDesignersChoiceProduct() && !this.isPostPurchaseJersey()) {
      this.moulding.permalink = this.product.handle;
    }

    await this.updateVariantAndConstraints();

    // Legacy shortcut
    this.fixedSize = this.isFixedSizeProduct();
    this.catalog.shopifyQuickShipProduct = this.isShopifyQuickShipProduct();
    await this.calculatePrice();

    return this;
  }

  // Use this when you intend to use both the new and the old frameSpecs
  // separately, so each gets its own joinery FS-num and database id
  duplicate () {
    const clonedCopy = this.clone();
    delete clonedCopy.id;
    delete clonedCopy.number;
    return clonedCopy;
  }

  // Use this when you want a copy of the frameSpec for validation or
  // modification but you don't need to generate a new FS-number. For example,
  // you make a clone, make some changes, validate those changes, and then
  // start using the clone instead of the original, overwriting the original in
  // joinery when you call .saveToServer().
  clone () {
    return new FrameSpec(cloneDeep(this), this.joineryToken, this.joineryHost, this.shopifyHost, true);
  }

  // FrameSpec Generators ↑
  // FrameSpec Getters ↓

  isValid (options) {
    const defaultValidations = {
      mats: true,
      mount: true,
      artworks: true,
      moulding: true
    };

    const validations = { ...defaultValidations, ...options };

    const results = {
      mats: validations.mats ? frameSpecValidators.validMatsSpec(this.mats) : true,
      mount: validations.mount ? frameSpecValidators.validMountSpec(this.mount) : true,
      artworks: validations.artworks ? frameSpecValidators.validArtworksSpec(this.artworks) : true,
      moulding: validations.moulding ? frameSpecValidators.validMouldingSpec(this.moulding) : true
    };

    const valid = results.mats && results.mount && results.artworks && results.moulding;

    if (!valid) {
      this.errors.validity = results;
    } else {
      this.errors.validity = null;
    }

    return valid;
  }

  isInStoreFulfillable () {
    return Boolean(this.product && this.product.tags && this.product.tags.includes('In-Store Fulfillable'));
  }

  async isManufacturable () {
    try {
      const result = (await axios.post(
        `${this.joineryHost}/api/v1/framing_specification_pricings`,
        {
          framing_specification: this
        })).data;

      if (!result.valid) {
        this.errors.manufacturability = [result.errors];
      }

      return result.valid;
    } catch (error) {
      this.errors.manufacturability = [error];

      return false;
    }
  }

  isCustomProduct () {
    if (!this.product) {
      return true;
    }
    return this.product.productType === "Custom Frame" || this.isPostPurchaseDesignersChoiceProduct();
  }

  isCuratedDesignFrame () {
    return !Boolean(this.isCustomProduct() || this.isGetThisLookProduct());
  }

  isObject () {
    return this.productCategory() === "object";
  }

  isCustomJerseyProduct () {
    return this.isJersey() && this.isCustomProduct();
  }

  isCuratedLayoutJerseyProduct () {
    if (!this.isCuratedDesignFrame() || !this.isJersey()) {
      return false;
    }
    const retailJerseyId = this.getRetailJerseyProductId();
    const croppedJerseyId = this.getCroppedJerseyProductId();
    const currentProductId = this.catalog.shopifyProductId;

    return currentProductId === retailJerseyId || currentProductId === croppedJerseyId;
  }

  isCroppedJerseyProduct () {
    return this.isCuratedLayoutJerseyProduct()
      && this.catalog.shopifyProductId === this.getCroppedJerseyProductId();
  }

  isCuratedLayoutGolfBallProduct () {
    if (!this.isCuratedDesignFrame() || this.productCategory() !== "object") {
      return false;
    }

    const productCategorySubtype = this.getArtwork()?.productCategorySubtype;
    if (productCategorySubtype !== "golf-ball") {
      return false;
    }

    return this.catalog.shopifyProductId === this.getRetailGolfBallProductId();
  }

  isPhysicalCuratedProduct () {
    return this.isCuratedLayoutJerseyProduct() || this.isCuratedLayoutGolfBallProduct();
  }

  isShopifyQuickShipProduct() {
    return this.hasProductTag("Quick Ship");
  }

  isFixedSizeProduct () {
    return !Boolean(this.isCustomProduct() || this.isGetThisLookProduct());
  }

  isGetThisLookProduct () {
    if (this.product) {
      return this.product.productType === "Get This Look";
    }
  }

  originatedAsGetThisLookProduct () {
    return Boolean(this.catalog.shopifyGetThisLookProductId);
  }

  async retrieveGetThisLookProduct () {
    if (this.catalog.shopifyGetThisLookProductId) {
      return await this.retrieveProductById(this.catalog.shopifyGetThisLookProductId);
    } else {
      return null;
    }
  }

  async retrieveGetThisLookVariant () {
    if (this.catalog.shopifyGetThisLookProductId && this.catalog.shopifyGetThisLookVariantId) {
      const product = await this.retrieveProductById(this.catalog.shopifyGetThisLookProductId);
      const variant = product.variants.find((variant) => variant.shopifyVariantId === this.catalog.shopifyGetThisLookVariantId);
      const frameSpecObj = await FrameSpec.build({
        frameSpecData: variant.frameSpec,
        joineryHost: this.joineryHost,
        shopifyHost: this.shopifyHost
      });
      variant.frameSpec = frameSpecObj;

      return variant;
    } else {
      return null;
    }
  }

  isPrintableArtProduct () {
    return this.product && this.product.productType === "Art Print Frame";
  }

  isReadymadeProduct () {
    return Boolean(this.product && this.product.readymade);
  }

  isReadymadeAcrylicProduct () {
    return Boolean(
      (this.variant && this.variant.sku && this.variant.sku.startsWith("RM")) ||
      (this.mount && this.mount.name === acrylicMatName && !this.mount.elevated)
    );
  }

  isQuickShipProduct () {
    return false;
  }

  isDesignersChoiceProduct () {
    return Boolean(
      (this.product && this.product.is_designers_choice) ||
      (this.product && this.product.productType === "Designers Choice")
    );
  }

  isPostPurchase() {
    return Boolean(this.orderNumber || this.lineItemNumber);
  }

  isPostPurchaseDesignersChoiceProduct () {
    return Boolean(this.designersChoiceRequested && this.isPostPurchase());
  }

  isPostPurchaseJersey () {
    return Boolean(this.isJersey() && this.isPostPurchase());
  }

  croppingMaskUrl () {
    if (this.variant && this.variant.croppingMask && this.variant.croppingMask.imageWhiteUrl) {
     return this.variant.croppingMask.imageWhiteUrl;
    } else {
      return null; 
    }
  }

  isHeartstagramMat () {
    if (this.mats[0]) {
      return Boolean(this.mats[0].name === heartstagramMatName);
    } else {
      return false;
    }
  }

  isTableTopFrame () {
    return this.hasProductTag("Tabletop Frames");
  }

  getArtTypeForLineItemProperties () {
    if (this.isTableTopFrame() || this.isOrnamentFrame() || this.isReadymadeProduct() || this.isDigital()) {
      return "Digital Photos";
    }
    
    const productCategory = this.productCategory();
    if (productCategory && categoryMap[productCategory]) {
      return categoryMap[productCategory];
    }

    const conveyance = this.variant.selectedOptions
      && this.variant.selectedOptions.find((option) => option.name === "Conveyance");
    return conveyance && conveyance.value ? `${conveyance.value} Art` : undefined;
  }

  isOrnamentFrame () {
    const sku = this.moulding && this.moulding.joinerySku;
    const isReadymadeMini = sku && (
      sku.startsWith('RM-') && (sku.includes('-MINI-') || sku.endsWith('-MINI'))
    );
    const isOrnament = this.hasProductTag("Ornament") || this.moulding.permalink.includes("ornament");
    return isReadymadeMini || isOrnament;
  }

  hasProductTag (tag) {
    return Boolean(this.product && this.product.tags && this.product.tags.includes(tag));
  }

  productCategory () {
    const artwork = this.artworks && this.artworks[0];
    if (!artwork) return null;

    const { productCategory } = artwork;
    if (typeof productCategory === "string") {
      return productCategory;
    } else if (productCategory && productCategory.slug) {
      return productCategory.slug;
    } else {
      return null;
    }
  }

  productCategoryDisplay () {
    const productCategory = this.productCategory();
    if (productCategory && categoryMap[productCategory]) {
      return categoryMap[productCategory];
    }
    return productCategory;
  }

  conveyance () {
    return this.artworks[0] ? this.artworks[0].conveyance : null;
  }

  isDigital () {
    return DIGITAL_CONVEYANCES.includes(this.conveyance());
  }

  isPhysical () {
    return PHYSICAL_CONVEYANCES.includes(this.conveyance());
  }

  conveyanceType ({ fullName = false } = {}) {
    return this.isDigital()
      ? `Digital${fullName ? ' Photo' : ''}`
      : `Physical${fullName ? ' Art' : ''}`;
  }

  isJersey () {
    return this.productCategory() === "jersey";
  }


  getJerseyLayoutType() {
    const productTitle = this.product?.title || '';

    // Check if title contains layout indicators
    if (productTitle.toLowerCase().includes('floated') || productTitle.toLowerCase().includes('float')) {
      return 'classic-float';
    } else if (productTitle.toLowerCase().includes('cropped')) {
      return 'classic-cropped';
    }

    return 'classic-float';
  }

  isCanvas () {
    return this.productCategory() === "canvas";
  }

  isCanvasOrCanvasPaper () {
    return this.productCategory() === "canvas" || this.productCategory() === "canvas_paper";
  }

  isMultiArtwork () {
    return Boolean(
      this.variant &&
      this.variant.frameSpec &&
      this.variant.frameSpec.mats[0] &&
      isMatMultiOpening(this.variant.frameSpec.mats[0].name)
    );
  }

  async matPresentation () {
    await this.getAllFullMats();

    if (this.isFloated()) {
      if (this.isClearFloated()) {
        const acrylicMat = this._allFullMats.find(mat => mat.name === acrylicMatName);
        return acrylicMat ? acrylicMat.presentation : "Clear Float";
      } else {
        const floatMountColor = this._allFullMats.find(m => m.name == this.mount.name);
        return floatMountColor ? `Float Mount - ${floatMountColor.presentation}` : "Float Mount";
      }
    }
    if ((!this.isDesignersChoiceProduct() && this.hasMat()) ||
        (this.isDesignersChoiceProduct() && this.hasMat() && this.isPostPurchase())
    ) {
      let matPresentation = "";
      const matOption = this._allFullMats.find(m => m.name == this.mats[0].name);
      if (matOption) {
        matPresentation = matOption.presentation;
      }

      let accentMatPresentation = "";
      if (this.hasAccentMat()) {
        const accentMatOption = this._allFullMats.find(m => m.name == this.mats[1].name);
        if (accentMatOption) {
          accentMatPresentation = `${accentMatOption.presentation} Accent Mat`;
        }
      }

      return [matPresentation, accentMatPresentation].filter(Boolean).join(", ");
    }

    return "N/A";
  }

  frameStyle () {
    if (this.isCuratedDesignFrame()) {
      return this.variant.frameStyle;
    } else {
      return this.product.productSubtitle;
    }
  }

  setMatStyle (mat) {
    if (mat.name === floatMountMatName) {
      this.removeMats();
    } else if (mat.name === acrylicMatName) {
      this.removeMats();
      this.removeAccentMats();
      this.removeStoryPocket();
      this.removeCustomKraftPaper();
      this.setHangingHardwareToDefault();
    } else if (mat.name === noMatName) {
      this.removeMats();
    } else {
      this.setMat(mat);
    }

    this.setMount(mat);

    const removeMatCaptionStyles = ["linen", "striped", "silk", "metallic"];
    if (removeMatCaptionStyles.includes(mat.style)) {
      this.removeMatCaption();
    }

    this.setupNoAcrylic();
  }

  hasMat () {
    return Boolean(this.mats[0]);
  }

  matStyleSlug () {
    if (this.isClearFloated()) return "clear-float";
    if (this.isFloated()) return "float";
    if (!this.hasMat()) return "no-mat";
    return this.mats[0].matStyle || "classic";
  }

  hasAccentMat () {
    return Boolean(this.mats[1]);
  }

  hasMatStyle (style) {
    return Boolean(this.mats[0] && this.mats[0].matStyle === style);
  }

  hasEightPlyMat () {
    return this.hasMatStyle("8-ply");
  }

  hasLinenMat () {
    return this.hasMatStyle("linen");
  }

  hasSilkMat () {
    return this.hasMatStyle("silk");
  }

  hasArtDecoMat () {
    return this.hasMatStyle("art-deco");
  }

  hasFarrowAndBallMat () {
    return this.hasMatStyle("farrow-and-ball");
  }

  hasGinghamMat () {
    return this.hasMatStyle("gingham");
  }

  hasStripedMat () {
    return Boolean(this.mats[0] && stripedMats.includes(this.mats[0].name));
  }

  hasPremiumMat () {
    return Boolean(
      this.price &&
      this.price.price_breakdown &&
      this.price.price_breakdown.find((item) => item.key === "premium_mat")
    );
  }

  hasMetallicMat () {
    // these may get moved to their own style, if so we can add a style and check for metallic style here
    return Boolean(this.mats[0] && (this.mats[0].name === "E4213P" || this.mats[0].name === "11-064"));
  }

  hasMatCaption () {
    return Boolean(this.hasMat() && this.mats[0].matCaption);
  }

  hasAsymmetricalMat () {
    if (!this.hasMat()) {
      return false;
    }

    return Boolean(new Set(Object.keys(this.mats[0].reveal).map((key) => this.mats[0].reveal[key])).size > 1); // Thanks Copilot
  }

  hasMatCustomizations () {
    if (!this.hasMat()) {
      return false;
    }

    return this.hasMatCaption() ||
      this.hasAccentMat() ||
      this.hasPremiumMat() ||
      this.hasAsymmetricalMat();
  }

  isFloated () {
    if (!this.mount) return false;
    if (this.mount.elevated) return true;
    if (!this.mount.reveal) return false;
    const revealTotal = this.mount.reveal.topInInches +
      this.mount.reveal.rightInInches +
      this.mount.reveal.bottomInInches +
      this.mount.reveal.leftInInches;
    return revealTotal > 0;
  }

  isRaisedFloated () {
    return this.isFloated() && Boolean(this.mount.elevated) && !this.isClearFloated();
  }

  isFlushFloated () {
    return this.isFloated() && !this.mount.elevated;
  }

  getReveal () {
    return this.isFloated() ? this.mount?.reveal : this.mats?.[0]?.reveal;
  }

  getRevealRatios () {
    const ratios = this.finalFrameSize?.ratios;
    if (this.finalFrameSize?.locked && ratios && ratios.x && ratios.y) {
      return ratios;
    }

    const reveal = this.getReveal();
    if (!reveal) {
      return { x: 1, y: 1 };
    }

    return {
      x: reveal.rightInInches ? reveal.leftInInches / reveal.rightInInches : 1,
      y: reveal.bottomInInches ? reveal.topInInches / reveal.bottomInInches : 1
    };
  }

  isClearFloated () {
    return this.isFloated() && this.mount.name === acrylicMatName;
  }

  validFloatSize () {
    const exterior = this.getArtwork().exterior;
    const longSide = Math.max(exterior.widthInInches, exterior.heightInInches);
    const shortSide = Math.min(exterior.widthInInches, exterior.heightInInches);
    if (shortSide <= 29 && longSide <= 37) {
      return true;
    } else {
      return false;
    }
  }

  categoryRequiresFloat () {
    const productCategory = this.productCategory()
    const floatedCategories = ["textile", "object"];
    return Boolean(floatedCategories.includes(productCategory));
  }

  isOversized () {
    if (this.hasArtwork()) {
      const finalFrameSize = this.finalFrameSize || {};
      const minFFSDim = Math.min(finalFrameSize.width, finalFrameSize.height);
      const maxFFSDim = Math.max(finalFrameSize.width, finalFrameSize.height);

      const { maxDim, minDim } = OVERSIZE_DIMENSIONS;

      return minFFSDim > minDim || maxFFSDim > maxDim;
    } else {
      return false;
    }
  }

  isTooLargeForShiping () {
    return this.isOversized();
  }

  requiresInStorePickup () {
    if (!this.hasArtwork()) {
      return false;
    }

    // e.g. sizeGroup.name = 'XG' or 'MG' (extra large or mega giant) → returns true
    const sizeGroup = this.sizeGroup();
    if (sizeGroup && IN_STORE_PICKUP_SIZE_GROUPS.includes(sizeGroup.name)) {
      return true;
    }

    return this.isShipToHomeRestricted();
  }

  isShipToHomeRestricted () {
    if (SHIP_TO_HOME_RESTRICTED_SIZE_GROUPS.includes(this.sizeGroup()?.name)) {
      return true;
    }

    if (this.hasArtwork()) {
      const { width, height } = this.finalFrameSize || {};
      if (width == null || height == null) {
        return false;
      }

      const { maxDim, minDim } = SHIP_TO_HOME_RESTRICTION_DIMENSIONS;

      return Math.min(width, height) >= minDim || Math.max(width, height) >= maxDim;
    } else {
      return false;
    }
  }

  outerMatWidth () {
    if (this.isFloated()) {
      return this.mount.reveal.rightInInches + this.mount.reveal.leftInInches + this.artworks[0].exterior.artworkOpeningWidthInInches;
    } else if (this.hasMat()) {
      return this.mats[0].reveal.rightInInches + this.mats[0].reveal.leftInInches + this.artworks[0].exterior.artworkOpeningWidthInInches;
    } else {
      return this.artworks[0].exterior.artworkOpeningWidthInInches;
    }
  }

  outerMatHeight () {
    if (this.isFloated()) {
      return this.mount.reveal.topInInches + this.mount.reveal.bottomInInches + this.artworks[0].exterior.artworkOpeningHeightInInches;
    } else if (this.hasMat()) {
      return this.mats[0].reveal.topInInches + this.mats[0].reveal.bottomInInches + this.artworks[0].exterior.artworkOpeningHeightInInches;
    } else {
      return this.artworks[0].exterior.artworkOpeningHeightInInches;
    }
  }

  largestOuterMatDimension () {
    return Math.max(this.outerMatWidth(), this.outerMatHeight());
  }

  smallestOuterMatDimension () {
    return Math.min(this.outerMatWidth(), this.outerMatHeight());
  }

  hasArtwork () {
    if (!this.artworks) {
      return false;
    }

    if (
      this.variant &&
      this.variant.mat &&
      this.variant.frameSpec &&
      isMatMultiOpening(this.variant.frameSpec.mats[0].name)
    ) {
      return Boolean(
        this.artworks[0] &&
        this.artworks[0].completedMafMatName &&
        this.variant.frameSpec.mats[0].name === this.artworks[0].completedMafMatName
      );
    } else {
      return Boolean(
        this.artworks[0] &&
        this.artworks[0].artworkId &&
        frameSpecValidators.validArtworkSpec(this.artworks[0])
      );
    }
  }

  hasTemplateArtwork () {
    const artwork = this.getArtwork();
    if (!artwork || !this.product) {
      return false;
    }

    let possibleVariants = this.product.variants.filter(v => v.frameSpec);
    if (!possibleVariants.length) {
      return false;
    } else {
      const matchingArtwork = possibleVariants.filter( v => v.frameSpec.artworks[0].artworkId === artwork.artworkId );
      return matchingArtwork.length > 0;
    }
  }

  needsCrop () {
    if (this.isPrintableArtProduct() || !this.hasArtwork()) {
      return false;
    }

    const firstArtwork = this.getArtwork();

    const expectedFixedSizeRatio =
      firstArtwork.exterior.widthInInches / firstArtwork.exterior.heightInInches;

    const pixelWidth = firstArtwork.customerSpecified.dimensionsInPixels
      ? firstArtwork.customerSpecified.dimensionsInPixels.width
      : firstArtwork.originalUnedited.dimensionsInPixels.width;
    const pixelHeight = firstArtwork.customerSpecified.dimensionsInPixels
      ? firstArtwork.customerSpecified.dimensionsInPixels.height
      : firstArtwork.originalUnedited.dimensionsInPixels.height;

    const artworkRatio = pixelWidth / pixelHeight;

    return Boolean(Math.abs(artworkRatio - expectedFixedSizeRatio) > 0.001);
  }

  canPrintArtwork () {
    if (!this.hasArtwork() || this.isCustomProduct() || this.isPrintableArtProduct()) {
      return true;
    }
    const firstArtwork = this.getArtwork();

    const artworkWidth = firstArtwork.exterior.widthInInches;
    const artworkHeight = firstArtwork.exterior.heightInInches;

    let longSide = artworkWidth;
    let shortSide = artworkHeight;

    if (artworkHeight >= artworkWidth) {
      longSide = artworkHeight;
      shortSide = artworkWidth;
    }

    const pixelWidth = firstArtwork.customerSpecified.dimensionsInPixels
      ? firstArtwork.customerSpecified.dimensionsInPixels.width
      : firstArtwork.originalUnedited.dimensionsInPixels.width;
    const pixelHeight = firstArtwork.customerSpecified.dimensionsInPixels
      ? firstArtwork.customerSpecified.dimensionsInPixels.height
      : firstArtwork.originalUnedited.dimensionsInPixels.height;

    const sizeMatch = proportionalArtworkDimensions(
      pixelWidth,
      pixelHeight,
      shortSide,
      longSide
    );

    return Boolean(sizeMatch.length);
  }
  
  // This is a lightweight method to check if the artwork can be printed in the selected frame
  // Thsis utility is intended to be used in the collectin pages to avoid unnecessary crop operations
  canPrintArtworkInSelectedFrame () {
    if (!this.hasArtwork() || this.isCustomProduct() || this.isPrintableArtProduct()) {
      return true;
    }
    
    const firstArtwork = this.getArtwork();
    
    // Crop dimensions to match frame ratio
    const artworkWidth = firstArtwork.exterior.widthInInches;
    const artworkHeight = firstArtwork.exterior.heightInInches;
    
    const originalArtworkWidth = firstArtwork.originalUnedited.dimensionsInPixels.width;
    const originalArtworkHeight = firstArtwork.originalUnedited.dimensionsInPixels.height; 
    
    const artworkRatio = artworkWidth / artworkHeight;
    const originalArtworkRatio = originalArtworkWidth / originalArtworkHeight;

    let finalWidth = originalArtworkWidth;
    let finalHeight = originalArtworkHeight;

    if (artworkRatio > originalArtworkRatio) {
      finalHeight = originalArtworkWidth / artworkRatio;
    } else {
      finalWidth = originalArtworkHeight * artworkRatio;
    }
    
    firstArtwork.customerSpecified.dimensionsInPixels = { width: finalWidth, height: finalHeight };

    // Check if cropped dimensions meet minimum PPI requirements
    const exteriorWidth = firstArtwork.exterior.widthInInches;
    const exteriorHeight = firstArtwork.exterior.heightInInches;
    
    if (finalWidth / exteriorWidth < minimumPPI || finalHeight / exteriorHeight < minimumPPI) {
      return false;
    }
    
    return true;
  }

  getArtwork (index = 0) {
    return this.artworks ? this.artworks[index] : null;
  }

  getOrientation () {
    if (this.getArtwork()) {
      const width = this.getArtwork().exterior.widthInInches;
      const height = this.getArtwork().exterior.heightInInches;

      if (width > height) {
        return "landscape";
      } else if (height > width) {
        return "portrait";
      } else {
        return "square";
      }
    } else {
      return null;
    }
  }

  getCatalogVariant () {
    return this.product.variants.find(variant => variant.shopifyVariantId === this.catalog.shopifyVariantId);
  };

  getSameFrameStyleVariants () {
    const catalogVariant = this.getCatalogVariant();
    if (!catalogVariant) {
      return [];
    } else {
      return this.product.variants.filter(variant => variant.frameStyle === catalogVariant.frameStyle);
    }
  };

  sizeGroup () {
    let widthInInches, heightInInches;

    if (
      this.artworks
      && this.artworks[0]
      && this.artworks[0].exterior
      && this.artworks[0].exterior.widthInInches
      && this.artworks[0].exterior.heightInInches
    ) {
      widthInInches = this.artworks[0].exterior.widthInInches;
      heightInInches = this.artworks[0].exterior.heightInInches;
    }

    let conveyance;
    if (this.isCustomProduct()) {
      conveyance = "uploaded";
    } else if (this.artworks[0]) {
      conveyance = this.artworks[0].conveyance
    }

    return determineSizeGroup(widthInInches, heightInInches, conveyance);
  }

  checkValidVariantForSizeGroup (moulding) {
    if (!this.sizeGroup() || this.isCuratedDesignFrame() || this.isJersey()) {
      return null;
    };
    const sizeGroup = this.sizeGroup().name;
    let possibleVariants = [...moulding.variants];

    possibleVariants = possibleVariants.filter( v => v.selectedOptions[0].value === sizeGroup );
    const variant = possibleVariants[0];

    if (possibleVariants.length === 0) {
      return "This moulding is not available in this size";
    } else if (variant.inventoryQuantity < 1 && variant.inventoryPolicy !== "CONTINUE") {
      return "This moulding is currently out of stock";
    }

  }

  urlParams (options = {}) {
    // Flatten artworks to ensure productCategory is a string slug, not an object
    const flattened = urlFrameSpecWriter._flattenArtworks(this);

    if (options.stringify) {
      return urlFrameSpecWriter.frameSpecToQuery(flattened);
    } else {
      return urlFrameSpecWriter.buildParamsObject(flattened, urlFrameSpecMap.shortFrameSpecMap);
    }
  }

  pathWithParams (pathWithParams, params = {}) {
    const dummyBase = 'https://mock';
    const url = new URL(pathWithParams, dummyBase);

    for (const [key, value] of Object.entries(params)) {
      url.searchParams.set(key, value);
    }

    return url.pathname + url.search;
  }
  pathWithFrameSpec (pathWithParams, params = {}) {
    const dummyBase = 'https://mock';
    const url = new URL(pathWithParams, dummyBase);
    const newUrl = new URL(`${url.pathname}?${this.urlParams({ stringify: true })}`, dummyBase);

    return this.pathWithParams(newUrl.pathname + newUrl.search, {
      ...Object.fromEntries(url.searchParams.entries()),
      ...params
    });
  }

  async getMountingTypes () {
    try {
      const response = await axios.post(
        `${this.joineryHost}/api/v1/framing_specification_mounting_types`,
        { framing_specification: this.compactFrameSpec() }
      );

      return response.data?.mounting_types || [];
    } catch (error) {
      console.error("Error from getMountingTypes", error);
      return [];
    }
  }

  compactFrameSpec () {
    // reduces size of frameSpec for api calls
    const compactedSpec = omit(this, ['_allMouldingOptions', '_usedFullMats', '_allFullMats', 'price', 'product', '_matPresentations', '_collectionData', '_colorSwatches']);
    return compactedSpec;
  }

  async calculatePrice () {
    if (this.product) {
      if (
        this.isReadymadeProduct() &&
        !this.isValid({ artworks: false, mats: false })
      ) {
        this.price = null;
        return null;
      } else if (!this.isReadymadeProduct() && !this.isValid({ artworks: false })) {
        this.price = null;
        return null;
      }

      try {
        const variantId = !this.isCustomProduct() && this.variant ? this.variant.id : null;

        const pricingResponse = await axios.post(
          `${this.joineryHost}/api/v1/framing_specification_pricings`, {
            variant_id: variantId,
            framing_specification: this.compactFrameSpec()
          }
        );
        this.price = pricingResponse.data;
        return pricingResponse.data;
      } catch (error) {
        console.error("Error from setPriceFromFrameSpec");
        this.price = null;
        return error;
      }
    } else {
      try {
        const pricingResponse = await axios.post(
          `${this.joineryHost}/api/v1/framing_specification_pricings`,
          { framing_specification: this.compactFrameSpec() }
        );
        
        this.price = pricingResponse.data;
        return pricingResponse.data;
      } catch (error) {
        console.error("Error from setPriceFromFrameSpec", this.moulding.permalink);
        this.price = null;
        return error;
      }
    }
  }

  async getMatPresentations () {
    const matPresentationsMap = {
      'Window Mat': 'classic',
      'No Mat': 'no-mat',
      'Raised Float': 'raised-float',
      'Clear Float': 'clear-float',
      'Flush Float': 'flush-float'
    };

    try {
      const response = await axios.post(
        `${this.joineryHost}/api/v1/framing_specification_mat_presentations`,
        { framing_specification: this.compactFrameSpec() }
      );

      const matPresentations = response.data?.mat_presentations || [];
      this._matPresentations = matPresentations.map(presentation => matPresentationsMap[presentation]);
      return this._matPresentations;
    } catch (error) {
      console.error("Error from getMatPresentations", error);
      return [];
    }
  }

  async finalFrameSizeGeometry () {
    const downToNearestEighth = (n) => Math.floor(n / finalFrameSizeIncrement) * finalFrameSizeIncrement;
    const artwork = this.artworks?.[0]?.exterior;
    const fullMoulding = await this.getCurrentMouldingOption();

    const openingWidth = downToNearestEighth(artwork?.artworkOpeningWidthInInches || artwork?.widthInInches);
    const openingHeight = downToNearestEighth(artwork?.artworkOpeningHeightInInches || artwork?.heightInInches);

    const mouldingWidth = this.isCanvas()
      ? ((fullMoulding.mouldingWidth || 0) - (fullMoulding.rabbetWidth || 0) + (fullMoulding.canvasGapWidth || 0))
      : (fullMoulding.mouldingWidth || 0);
    const mouldingContribution = 2 * mouldingWidth + (fullMoulding.assemblyAllowance || 0);

    return { openingWidth, openingHeight, mouldingContribution };
  }

  async finalFrameSizeBoundaries () {
    if (this.isClearFloated()) {
      const fixed = await this.calculateFinalFrameSizeLocally(this.mount.reveal);
      return { min: fixed, max: fixed };
    }

    if (!this.hasMat() && !this.isFloated()) {
      const fixed = await this.calculateFinalFrameSizeLocally(this.revealForWidth(0));
      return { min: fixed, max: fixed };
    }

    const limits = this.isFloated() ? mountLimits : matLimits;

    const { x: xRatio, y: yRatio } = this.getRevealRatios();
    const { openingWidth, openingHeight, mouldingContribution } = await this.finalFrameSizeGeometry();

    const revealTolerance = matRevealIncrement / 2;

    const validRange = (opening, ratio) => {
      const smallerSideFactor = ratio >= 1 ? 1 + ratio : (1 + ratio) / ratio;
      const largerSideFactor = ratio >= 1 ? (1 + ratio) / ratio : 1 + ratio;

      const minRevealSpace = (limits.min - revealTolerance) * smallerSideFactor;
      const maxRevealSpace = (limits.max + revealTolerance) * largerSideFactor;

      const mouldingAndOpening = opening + mouldingContribution;

      return {
        lowest: Math.ceil((mouldingAndOpening + minRevealSpace) / finalFrameSizeIncrement) * finalFrameSizeIncrement,
        highest: Math.floor((mouldingAndOpening + maxRevealSpace) / finalFrameSizeIncrement) * finalFrameSizeIncrement
      };
    };

    const widthRange = validRange(openingWidth, xRatio);
    const heightRange = validRange(openingHeight, yRatio);

    return {
      min: { width: widthRange.lowest, height: heightRange.lowest },
      max: { width: widthRange.highest, height: heightRange.highest }
    };
  }

  async finalFrameSizeOptions () {
    const { min, max } = await this.finalFrameSizeBoundaries();

    if (!min.width || !max.width) {
      return { widths: [], heights: [] };
    }

    const fullMoulding = await this.getCurrentMouldingOption();
    // Cap at the larger of the normal and oversized manufacturable lengths.
    const normalMax = fullMoulding?.maxManufacturableLength || 0;
    const oversizedMax = fullMoulding?.oversizedMaxManufacturableLength || 0;
    const maxManufacturable = Math.max(normalMax, oversizedMax) || null;

    // 1/8" sizes up to the cap, raised to the current size for oversized frames.
    const options = (start, highestValue, current) => {
      const ceiling = Math.max(maxManufacturable || 0, current || 0) || highestValue;
      const top = Math.min(highestValue, ceiling);

      let opts = [];
      if (top >= start) {
        const steps = Math.round((top - start) / finalFrameSizeIncrement);
        opts = Array.from({ length: steps + 1 }, (_, i) => start + i * finalFrameSizeIncrement);
      }

      if (current != null && !opts.some(value => Math.abs(value - current) < finalFrameSizeIncrement / 2)) {
        opts = [...opts, current].sort((a, b) => a - b);
      }

      return opts.length ? opts : [start];
    };

    return {
      widths: options(min.width, max.width, this.finalFrameSize?.width),
      heights: options(min.height, max.height, this.finalFrameSize?.height)
    };
  }

  async calculateFinalFrameSizeLocally (reveal) {
    const downToNearestEighth = (n) => Math.floor(n / finalFrameSizeIncrement) * finalFrameSizeIncrement;

    const artwork = this.artworks?.[0]?.exterior;
    const artworkOpeningWidth = artwork?.artworkOpeningWidthInInches;
    const artworkOpeningHeight = artwork?.artworkOpeningHeightInInches;
    const fullMoulding = await this.getCurrentMouldingOption();

    if (!artworkOpeningWidth || !artworkOpeningHeight || !fullMoulding) {
      return { width: null, height: null };
    }

    if (!reveal) {
      reveal = this.getReveal();
    }

    const mouldingWidth = this.isCanvas()
      ? ((fullMoulding.mouldingWidth || 0) - (fullMoulding.rabbetWidth || 0) + (fullMoulding.canvasGapWidth || 0))
      : (fullMoulding.mouldingWidth || 0);
    const mouldingContribution = 2 * mouldingWidth + (fullMoulding.assemblyAllowance || 0);

    const width = downToNearestEighth(
      downToNearestEighth(artworkOpeningWidth) + (reveal?.leftInInches || 0) + (reveal?.rightInInches || 0) + mouldingContribution
    );
    const height = downToNearestEighth(
      downToNearestEighth(artworkOpeningHeight) + (reveal?.topInInches || 0) + (reveal?.bottomInInches || 0) + mouldingContribution
    );

    return { width, height };
  }

  calculateRevealSidesForAxis (frameSideLength, opening, mouldingContribution, ratio) {
    const space = frameSideLength - mouldingContribution - opening;
    return {
      weighted: Math.round((space * ratio / (1 + ratio)) / matRevealIncrement) * matRevealIncrement,
      base: Math.round((space / (1 + ratio)) / matRevealIncrement) * matRevealIncrement
    };
  }

  async recalculateForFinalFrameSize (finalFrameSize) {
    const ffs = finalFrameSize || this.finalFrameSize;

    const limits = this.isFloated() ? mountLimits : matLimits;

    if (!this.isFloated() && this.finalFrameSize?.ratios && !this.getPresetMatWidthOptions()?.weighted) {
      this.finalFrameSize.ratios = { x: 1, y: 1 };
    }

    const { openingWidth, openingHeight, mouldingContribution } = await this.finalFrameSizeGeometry();
    const { x: xRatio, y: yRatio } = this.getRevealRatios();

    const xSides = this.calculateRevealSidesForAxis(ffs.width, openingWidth, mouldingContribution, xRatio);
    const ySides = this.calculateRevealSidesForAxis(ffs.height, openingHeight, mouldingContribution, yRatio);
    const newReveal = {
      topInInches: ySides.weighted,
      rightInInches: xSides.base,
      bottomInInches: ySides.base,
      leftInInches: xSides.weighted,
    }

    if (Object.values(newReveal).some(v => !Number.isFinite(v))) {
      return 'incompatible-reveal';
    }

    if (this.isClearFloated()) {
      const presetOptions = this.getPresetMatWidthOptions();
      const matchesPreset = Object.values(presetOptions).some(
        (preset) =>
          newReveal.leftInInches === preset.leftInInches
          && newReveal.rightInInches === preset.rightInInches
          && newReveal.topInInches === preset.topInInches
          && newReveal.bottomInInches === preset.bottomInInches
      );
      if (!matchesPreset) {
        return 'incompatible-reveal';
      }
    }

    if ((!this.hasMat() && !this.isFloated()) && Object.values(newReveal).some(v => v !== 0)) {
      return 'incompatible-reveal';
    }

    if (Object.values(newReveal).some(v => v < limits.min)) {
      return 'below-limit';
    }
    if (Object.values(newReveal).some(v => v > limits.max)) {
      return 'above-limit';
    }

    if (this.isFloated()) {
      this.setMountReveal(newReveal);
    } else {
      this.setReveal(newReveal);
    }

    return false;
  }

  setFinalFrameSize (finalFrameSize = {}) {
    const ratios = this.getRevealRatios();

    this.finalFrameSize = {
      ...(this.finalFrameSize || {}),
      ...finalFrameSize,
      ratios,
      locked: true
    };
    this.allowFfsUpdate = true;
    this.syncFfsAssociateInstruction();
  }

  async removeFinalFrameSize () {
    this.finalFrameSize.locked = false;
    delete this.finalFrameSize.ratios;
    this.allowFfsUpdate = true;
    const changed = this.conformMatRevealToSizeGroup();
    if (changed) {
      this.calculatePrice();
    }
    await this.resetFinalFrameSize();
    this.syncFfsAssociateInstruction();
  }

  syncFfsAssociateInstruction () {
    const def = associateInstructionDefinitions.find((i) => i.tag === "FFS_requirement");
    if (!def) return;

    if (this.finalFrameSize?.locked && this.finalFrameSize.width && this.finalFrameSize.height) {
      const { width, height } = this.finalFrameSize;
      const instruction = {
        ...def,
        primaryQuestion: {
          ...def.primaryQuestion,
          required: false,
          response: true
        },
        followUpQuestions: def.followUpQuestions.map((q) => (
          q.type === "dimensions"
            ? { ...q, response: { widthInInches: width, heightInInches: height } }
            : q
        )),
        responseSummaryText: `${width}" x ${height}"`
      };
      this.setAssociateInstruction(instruction);
    } else {
      this.removeAssociateInstruction(def.name);
    }
  }

  async resetFinalFrameSize () {
    this.finalFrameSize = await this.calculateFinalFrameSize(true);
  }

  async calculateFinalFrameSize (overwrite = false) {
    if (this.finalFrameSize?.width && this.finalFrameSize?.height && this.finalFrameSize.locked && !overwrite) {
      return this.finalFrameSize;
    }
    const finalFrameSize = await this.calculateFinalFrameSizeLocally();
    this.finalFrameSize = {
      ...this.finalFrameSize,
      ...finalFrameSize
    };

    return this.finalFrameSize;
  }

  fixedFinalFrameSize () {
    return this.isClearFloated() || (!this.hasMat() && !this.isFloated()) || this.isCanvas();
  }

  // *************************
  // * Design Option Helpers *
  // *************************


  static async getAllMouldings(joineryHost, params = {}) {
    const noParams = Object.keys(params).length === 0;
    if (noParams && _mouldingsCache[joineryHost]) {
      return _mouldingsCache[joineryHost];
    }

    const fetchPromise = (async () => {
      try {
        const mouldingsUrl = `${joineryHost}/api/v1/joinery_mouldings.json`;
        const allMouldingsResult = await axios.get(mouldingsUrl, { params });

        // Only mouldings that have both joinery and shopify data
        const filteredMouldings = allMouldingsResult.data.mouldings
          .filter((moulding) => moulding.shopifyProductId)
          .filter((moulding) => moulding.shopifyAssets)
          .filter((moulding) => !(moulding.product && moulding.product.variants && moulding.product.variants[0] && moulding.product.variants[0].discontinued));

        return {
          ...allMouldingsResult.data,
          mouldings: filteredMouldings,
        };
      } catch (e) {
        if (noParams) delete _mouldingsCache[joineryHost];
        throw e;
      }
    })();

    if (noParams) _mouldingsCache[joineryHost] = fetchPromise;
    return fetchPromise;
  }

  async getMouldingOptions (collection_handle, forceRefetch = false) {
    try {
      if (!this._allMouldingOptions || forceRefetch) {
        const params = collection_handle ? { collection_handle } : {};
        const allMouldingsResult = await FrameSpec.getAllMouldings(this.joineryHost, params);

        this._allMouldingOptions = allMouldingsResult.mouldings;

        this._collectionData = allMouldingsResult.collection;
        this._colorSwatches = allMouldingsResult.colorSwatches;
      }

      if (this.isCustomProduct()) {
        return {
          mouldings: this._allMouldingOptions,
          collection: this._collectionData,
          colorSwatches: this._colorSwatches
        };
      } else {
        // TODO: Ideally this would use joinerySku instead of the legacy spree permalink
        // But too many things assume frameSpec.moulding.permalink is a thing
        const availableMouldings = this._allMouldingOptions.filter((moulding) => {
          return this.product.variants.find((variant) => {
            return !variant.discontinued && variant.frameSpec.moulding.permalink === moulding.permalink;
          });
        });

        return {
          mouldings: availableMouldings,
          collection: this._collectionData,
          colorSwatches: this._colorSwatches
        };
      }
    } catch(error) {
      console.error("Error when fetching mouldings", error);
      this._allMouldingOptions = this._allMouldingOptions || [];
      this._colorSwatches = this._colorSwatches || {};
      return {
        mouldings: this._allMouldingOptions,
        collection: this._collectionData,
        colorSwatches: this._colorSwatches
      };
    }
  }

  async getJoineryMouldings() {
    const mouldingsUrl = `${this.joineryHost}/api/v1/mouldings.json`;
    const allMouldingsResult = await axios.get(mouldingsUrl);
    return allMouldingsResult.data;
  }

  async getArtworkSizeOptions () {
    if (this.isCustomProduct()) {
      // Calculate based on physics of artwork DPI, moulding
    } else {
      // Based on variants
      const artworkSizes = this.product.variants.map( (v) => v.frameSpec.artworks[0].exterior );
      return uniqBy(artworkSizes, size => `${size.widthInInches} x ${size.heightInInches}`)
    }
  }

  async getMatOptions () {
    // Returns mat options appropriate for the given frame type

    const isJerseyOrGolfBall = (this.isCuratedDesignFrame() && this.isJersey()) || this.isCuratedLayoutGolfBallProduct();

    if (!this.isCustomProduct() && !isJerseyOrGolfBall) {
      // tbd, we may not actually offer these options anywhere
      const primaryMatOptions = this.product.variants.map( (v) => v.frameSpec.mats[0] );
      const accentMatOptions = this.product.variants.map( (v) => v.frameSpec.mats[1] );
      return {
        primaryMatOptions: uniqBy(primaryMatOptions, mat => mat.name),
        accentMatOptions: uniqBy(accentMatOptions, mat => mat.name)
      }
    }

    await this.getAllFullMats();
    const matOptions = {
      allMatOptions: this._allFullMats,
      primaryMatOptions: this._allFullMats.filter(mat => !mat.disabled),
      accentMatOptions: this._allFullMats.filter(mat => !mat.fake && !mat.disabled && mat.canBeAccentMat),
      jumboMatOptions: this._allFullMats.filter(mat => !mat.disabled && mat.matBoardLongSide === 72),
      oversizedMatOptions: this._allFullMats.filter(mat => !mat.disabled && mat.matBoardLongSide === 60),
      baseMountOptions: this._allFullMats.filter(mat => !mat.fake && !mat.disabled && mat.canBeBaseMat)
    };

    if (this.isCuratedDesignFrame() && this.isJersey()) {
      return {
        ...matOptions,
        accentMatOptions: [],
        jumboMatOptions: [],
        oversizedMatOptions: []
      };
    }

    if (this.isCuratedLayoutGolfBallProduct()) {
      const excludedStyles = ['silk', 'linen', 'art-deco', '8-ply'];
      return {
        ...matOptions,
        primaryMatOptions: matOptions.primaryMatOptions.filter(mat =>
          !excludedStyles.includes(mat.style)
        ),
        accentMatOptions: [],
        jumboMatOptions: [],
        oversizedMatOptions: []
      };
    }

    return matOptions;
  }

  requiresJumboMats () {
    return this.largestOuterMatDimension() > 60 || this.smallestOuterMatDimension() > 40;
  }
  requiresJumboOrOversizedMats () {
    return this.largestOuterMatDimension() > 40 || this.smallestOuterMatDimension() > 32;
  }

  getMatsInSize () {
    if (this.requiresJumboMats()) {
      return this._allFullMats.filter(mat => mat.matBoardLongSide === 72);
    } else if (this.requiresJumboOrOversizedMats()) {
      return this._allFullMats.filter(mat => mat.matBoardLongSide === 72 || mat.matBoardLongSide === 60);
    } else {
      return this._allFullMats;
    }
  }

  getAvailableMats (showInternalMats = false) {
    const isJersey = this.isJersey();
    const mats = isJersey ? (this._allFullMats || []) : this.getMatsInSize();

    if (!isJersey && this.requiresJumboOrOversizedMats()) {
      return mats.filter(mat => !mat.disabled);
    }

    const isFloated = this.isFloated();
    const isOversized = this.isOversized();
    const isMultiArtwork = this.productCategory() === 'multi_artwork' || (this.artworks?.length || 0) > 1;
    const isGolfBall = this.isCuratedLayoutGolfBallProduct();
    const excludedGolfBallStyles = ['linen', 'silk'];

    return mats.filter(mat =>
      !mat.fake
      && !mat.disabled
      && !mat.parentId
      && !mat.oversized
      && (showInternalMats || !mat.internalOnly)
      && (isOversized ? mat.oversizedOnly : !mat.oversizedOnly)
      && (!isMultiArtwork || mat.multiCapable)
      && (!(isFloated || isJersey) || mat.canBeBaseMat)
      && (!isGolfBall || !excludedGolfBallStyles.includes(mat.style))
    );
  }

  getAvailableAccentMats (showInternalMats = false) {
    const mats = this.getMatsInSize();

    const isOversized = this.isOversized();

    return mats.filter(mat =>
      !mat.fake
      && !mat.disabled
      && !mat.parentId
      && mat.canBeAccentMat
      && !mat.oversized
      && (showInternalMats || !mat.internalOnly)
      && (isOversized ? mat.oversizedOnly : !mat.oversizedOnly)
    );
  }

  matNeedingShadeSelection () {
    if (this.isCroppedJerseyProduct() || !this._allFullMats) {
      return null;
    }

    const selectedNames = [
      this.isFloated() ? this.mount?.name : this.mats?.[0]?.name,
      this.mats?.[1]?.name
    ].filter(Boolean);

    const selectedMats = selectedNames.map(name => this._allFullMats.find(mat => mat.name === name));

    return selectedMats.find(mat => mat?.children?.length) || null;
  }

  matShadeSelectionIncomplete () {
    return Boolean(this.matNeedingShadeSelection());
  }

  allowsMat (allowOversize = false, allowJersey = false) {
   if (this.isCanvas()) {
     return {
      value: false,
      reason: "canvas category"
     };
   } else if (this.isJersey() && !allowJersey) {
     return {
      value: false,
      reason: "jersey category"
     }
   } else if (this.sizeGroup() && (!allowOversize && this.sizeGroup().name === "GR")) {
     return {
      value: false,
      reason: "size group"
     };
   } else {
     return {
      value: true
     }
   }
  }

  matMountStyleDescriptionText () {
    if (this.hasAccentMat()) {
      return "Accent mats are the perfect way to add a school or company color as a detail within a frame.";
    } else if (this.isClearFloated()) {
      return "Clear Float is a modern way to frame art with a clear border instead of a mat.";
    } else if (this.isFloated()) {
      return "Float mounting adds depth and shadow, perfect for showcasing torn or detailed edges.";
    } else if (this.hasEightPlyMat()) {
      return "8-ply mats give your art a bit of extra depth and shadow within the frame.";
    } else if (this.hasLinenMat()) {
      return "Linen mats are a soft textural and color pairing for your art.";
    } else if (this.hasSilkMat()) {
      return "Silk mats add soft color and a delicate sheen to your custom frame."
    } else if (this.hasMetallicMat()) {
      return "Metallic mats are shiny and reflective, a perfect pairing for pieces with gold or silver details."
    } else {
      return "";
    }
  }

  async getCurrentMouldingOption () {
    await this.getMouldingOptions();
    return this._allMouldingOptions.find(mouldingOption => mouldingOption.permalink == this.moulding.permalink);
  }

  async allowsRaisedFloat () {
    const currentMoulding = await this.getCurrentMouldingOption();
    return currentMoulding && currentMoulding.raisedFloatAllowed;
  }

  async allowsClearFloat () {
    const currentMoulding = await this.getCurrentMouldingOption();

    const productCategoriesThatAllowClearFloat = ["digital_photo", "original_art", "diplomas_and_certificates", "prints_and_posters", "photography", "newspaper_and_magazine", "multi_artwork"];
    const productCategoryAllowsClearFloat = productCategoriesThatAllowClearFloat.includes(this.productCategory());

    return currentMoulding && currentMoulding.clearFloatAllowed && productCategoryAllowsClearFloat;
  }

  async allowsEightPlyMat () {
    const currentMoulding = await this.getCurrentMouldingOption();
    return currentMoulding && currentMoulding.eightplyMatAllowed;
  }

  async allowsAccentMat () {
    const currentMoulding = await this.getCurrentMouldingOption();
    return currentMoulding && currentMoulding.accentMatAllowed;
  }

  async mouldingPlateAllowed () {
    const currentMoulding = await this.getCurrentMouldingOption();
    return currentMoulding && currentMoulding.mouldingPlateAllowed;
  }

  async allowsMouldingEngraving () {
    const currentMoulding = await this.getCurrentMouldingOption();
    return (currentMoulding && currentMoulding.mouldingEngravingAllowed) || (this.product && this.product.handle && this.product.handle.includes('fine-metal'));
  }

  storyPocketAllowed () {
    return Boolean(!this.isReadymadeAcrylicProduct() && !this.isClearFloated() && !this.isTableTopFrame());
  }

  async giftBoxSupportsMouldingHeight () {
    const currentMoulding = await this.getCurrentMouldingOption();
    return currentMoulding && currentMoulding.mouldingHeight <= 1.5;
  }

  getPresetMatWidthOptions () {
    const size = this.sizeGroup() && this.sizeGroup().name;
    if (this.isFloated()) {
      const presetSizes = presetMatWidths.sizes.float[size];
      const disallowedPresets = this.isClearFloated() ? ["thin"] : [];

      let filteredPresets = {};
      Object.entries(presetSizes).filter(
        (preset) => {
          if (!disallowedPresets.includes(preset[0])) {
            filteredPresets[preset[0]] = preset[1];
            return;
          } else {
            return;
          }
        }
      );
      return filteredPresets;
    } else {
      return presetMatWidths.sizes.regular[size];
    }
  }

  roomPreview () {
    const sizeGroupDefaultPositions = {
      XS: { x: 48, y: 55 },
      S: { x: 50, y: 55 },
      M: { x: 55, y: 55 },
      L: { x: 58, y: 55 },
      XL: { x: 60, y: 55 },
      GR: { x: 63, y: 55 }
    }
    const sizeGroup = this.sizeGroup();
    const initial = sizeGroup ? sizeGroupDefaultPositions[sizeGroup.name] : sizeGroupDefaultPositions["M"];
    const xMax = this.finalFrameSize ? 40 + (this.finalFrameSize.width / 2) : initial.x;
    const yMax = this.finalFrameSize ? 40 + (this.finalFrameSize.height / 2) : initial.y;

    return {
      enabled: true,
      url: "https://cdn.shopify.com/s/files/1/0727/7196/1122/files/preview_in_room-new-2024.jpg?v=1714413504",
      wallHeightInInches: 80,
      wallWidthInInches: 80,
      xMax: xMax,
      yMax: yMax
    };
  }

  showRendering () {
    return Boolean(frameSpecValidators.validFrameSpec(this));
  }

  primaryMaskedRendering () {
    return this.variant
      && this.variant.maskedRenderer
      && this.variant.maskedRenderer.length > 0
      && this.variant.maskedRenderer[0].primaryRendering;
  }

  clearConvexRenderUrl () {
    this.previewImage = null;
  }

  getConvexRenderUrl (maxQuality = false, refetch = false) {
    if (this.previewImage && this.previewImage.url && !refetch) {
      return this.previewImage.url;
    } else {
      const clone = this.clone();
      clone.getArtwork().maxQuality = maxQuality;
      return `${this.convexBaseUrl()}/preview_image?${clone.urlParams({ stringify: true })}`;
    }
  }

  async downloadPreview (maxQuality = false) {
    const url = this.getConvexRenderUrl(maxQuality);
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = blobUrl;
      a.download = 'frame-preview.jpg';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      window.open(url, '_blank');
    }
  }

  async getSurchargePrice (productId) {
    try {
      const product = await fetchSurchargeProductDetails(this.joineryHost, productId);
      if (!product) {
        return "25";
      }

      const sizeGroup = this.sizeGroup() && this.sizeGroup().name;
      const sizeGroupVariant = product.variants.find(v => v.selectedOptions[0].value === sizeGroup);
      return sizeGroupVariant ? `${parseFloat(sizeGroupVariant.price)}` : "25";
    } catch (error) {
      console.error("error fetching surcharge price", error);
      return "25";
    }
  }

  async getFloatPrice () {
    // fallback to standard $25, this should probably be removed after feb 2024 price changes
    const floatProductId = this.environment() === "production" ? "9056842613051" : "8149413364002";
    return this.getSurchargePrice(floatProductId);
  }

  async getAccentMatPrice () {
    const accentMatProductId = this.environment() === "production" ? "10038090039611" : "9663360368930";
    return this.getSurchargePrice(accentMatProductId);
  }

  async getPremiumMatPrice () {
    const premiumMatProductId = this.environment() === "production" ? "10038084895035" : "9663845826850";
    return this.getSurchargePrice(premiumMatProductId);
  }
	// FrameSpec Getters ↑
	// FrameSpec Setters and Mutations ↓

  async fetchUpdatedAt () {
    const authHeaders = { "Framebridge-Resource-Authorization-Token": this.authorizationToken };
    try {
      const result = await axios.get(`${this.joineryHost}/api/v1/framing_specifications/${this.number}/updated_at`, { headers: authHeaders });
      const updatedAt = result.data.updated_at;
      return updatedAt;
    } catch (error) {
      console.error('Error fetching updated_at:', error);
      throw error;
    }
  }

  async updateFromServer () {
    const authHeaders = { "Framebridge-Resource-Authorization-Token": this.authorizationToken };
    const result = await axios.get(`${this.joineryHost}/api/v1/framing_specifications/${this.number}`, { headers: authHeaders });
    const data = result.data;
    Object.assign(this, data);
    return this;
  }

  async saveToServer({regenerateId = false, processResponse = (data) => data} = {}) {
    let response;

    if (this.id && !regenerateId) {
      const path = `${this.joineryHost}/api/v1/framing_specifications/${this.id}`;
      const authHeaders = { "Framebridge-Resource-Authorization-Token": this.authorizationToken };
      const data = { framingSpecification: this.compactFrameSpec() };
      response = await axios.put(path, data, { headers: authHeaders });
    } else {
      const path = `${this.joineryHost}/api/v1/framing_specifications`;
      const data = {
        framingSpecification: this.compactFrameSpec() ,
        without_spree_order_populate: true, // This param is ignored in joinery, should be safe to remove soon.
        include_token: true
      };
      response = await axios.post(path, data);
    }
    if (response) {
      const data = processResponse({
        ...response.data,
        offsetInBundle: this.offsetInBundle
      }, this.joineryHost);
      Object.assign(this, data);
      delete this.allowFfsUpdate;
    }
  }

  async toCartItems ({ lineItemNumber, extraProperties } = {}) {
    if (this.templateIdentifier) {
      delete this.templateIdentifier;
    }

    await this.saveToServer({regenerateId: Boolean(lineItemNumber)});

    const extraItems = [];
    this.price.price_breakdown.forEach( (priceBreakdown) => {
      if (priceBreakdown.shopify_variant) {
        extraItems.push({
          id: priceBreakdown.shopify_variant,
          properties: {
            "_parent_design": this.number
          }
        });
      }
    });

    if (!this.finalFrameSize) {
      await this.calculateFinalFrameSize();
    }

    const priceIncludes = this.price.price_breakdown.filter((item) => {
      return !["fixed_variant_price", "float_mount"].includes(item.key);
    }).map((item) => {
      return item.presentation;
    });

    const includes = [
      ...priceIncludes,
      !this.acrylicRequired && this.acrylicRequired !== undefined && !this.customAcrylic
        ? 'No Acrylic' : undefined
    ].filter(Boolean).join(', ');

    const artType = this.getArtTypeForLineItemProperties();

    const previewUrl = this.getConvexRenderUrl(1000);

    const isPartOfMatchingSet = this.associateInstructions &&
      this.associateInstructions.tags &&
      this.associateInstructions.tags.includes("matching_set_instruction");

    const sizeAttrKey = this.isPhysical() ? "Art Size" : "Photo Size";
    const artworkSizeAndGroup = this.sizeGroup() && this.sizeGroup().presentation
      ? `${this.sizeGroup().presentation} • ${this.artworks[0].exterior.widthInInches}" x ${this.artworks[0].exterior.heightInInches}"`
      : `${this.artworks[0].exterior.widthInInches}" x ${this.artworks[0].exterior.heightInInches}"`;
    const finalFrameSize = this.finalFrameSize && this.finalFrameSize.width && this.finalFrameSize.height
      ? `${this.finalFrameSize.width}" x ${this.finalFrameSize.height}"`
      : undefined;

    let lineProperties = omitBy({
      "_artwork_item_quantity": this.isDigital() ? "" : String(this.getArtworkItemQuantity()),
      "_artwork_url": this.artworks[0].customerSpecified.url,
      "_frame_spec_authorization_token": this.authorizationToken,
      "_frame_spec_number": this.number,
      "_get_this_look_product_id": this.catalog.shopifyGetThisLookProductId,
      "_get_this_look_variant_id": this.catalog.shopifyGetThisLookVariantId,
      "_original_artwork_url": this.artworks[0].originalUnedited.url,
      "_oversized": this.isOversized() ? "true" : undefined,
      "_preview_url": previewUrl,
      "_production_category": this.original_production_category,
      "_product_category": this.productCategory(),
      "_quick_ship": this.catalog.shopifyQuickShipProduct,
      "_ship_to_home_restricted": this.isShipToHomeRestricted() ? "true" : undefined,
      [sizeAttrKey]: artworkSizeAndGroup,
      "Art Type": artType,
      "Final Frame Size": finalFrameSize,
      "Frame Style": this.frameStyle(),
      "Includes": includes,
      "Matching Set": isPartOfMatchingSet ? "True" : "",
      "Mat Style": await this.matPresentation(),
      ...(extraProperties || {})
    }, isEmpty);

    lineProperties = sortLineItemProperties(lineProperties);

    return [
      {
        id: this.catalog.shopifyVariantId,
        properties: lineProperties
      },
      ...extraItems
    ];
  }

  static getLocalShopifyCustomerId () {
    return window && window.SDG && window.SDG.Data && decode(window.SDG.Data.cid);
  }

  static async removeFromCart ({ lineItemNumber, shopifyCustomerId, cartHost }) {
    const localShopifyCustomerId = shopifyCustomerId || FrameSpec.getLocalShopifyCustomerId();
    const cartUrl = cartHost || cartUrlFallback;

    return await fetch(`${cartUrl}/api/cart/remove?cid=${encode(localShopifyCustomerId)}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        lineId: lineItemNumber
      })
    });
  }

  static async saveToCart ({ items, shopifyCustomerId, cartHost }) {
    const localShopifyCustomerId = shopifyCustomerId || FrameSpec.getLocalShopifyCustomerId();
    const cartUrl = cartHost || cartUrlFallback;

    return await fetch(`${cartUrl}/api/cart/add?cid=${encode(localShopifyCustomerId)}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items })
    });
  }

  async addToCart ({lineItemNumber, skipAutoCrop, shopifyCustomerId, extraProperties, cartHost} = {}) {
    if (this.giftBox && this.giftBox.explicitlyRemoved) {
      delete this.giftBox;
    }

    if (!skipAutoCrop) {
      await this.autoCrop({ saveToServer: true });
    }

    if (!this.shopifyHost) {
      const payload = {
        variant_id: this.variant.id,
        framingSpecification: this,
        line_item_number: lineItemNumber || getEditingLineItemNumber()
      };

      const response = await axios.post("/orders/populate.json", payload);
      return response;
    } else {
      await this.calculatePrice();

      const items = await this.toCartItems({ lineItemNumber, extraProperties });

      if (lineItemNumber) {
        await FrameSpec.removeFromCart({ lineItemNumber, shopifyCustomerId, cartHost });
      }

      return await FrameSpec.saveToCart({ items, shopifyCustomerId, cartHost });
    }
  }

  // TODO: Can we remove this method now? I don't see it being used anywhere.
  async setVariantToClosestVariant (optionId) {
    let possibleVariants = this.product.variants;

    const possibleVariantsForOptionId = possibleVariants.filter((variant) => {
      const optionValuesWithCorrectId = variant.option_value_ids.filter((optionValueId) => {
        return optionValueId === optionId;
      });

      return optionValuesWithCorrectId.length;
    });

    if (!possibleVariantsForOptionId.length) {
      this.variant = possibleVariants[0];
      await this.enforceFixedSizeVariant();
      return;
    }

    possibleVariants = possibleVariantsForOptionId;

    this.variant.option_value_ids.forEach((previousOptionValueId) => {
      const matchingVariants = possibleVariants.filter((possibleVariant) => {
        return possibleVariant.option_value_ids.includes(previousOptionValueId);
      });

      if (matchingVariants.length) {
        possibleVariants = matchingVariants;
      }
    });

    this.variant = possibleVariants[0];
    await this.enforceFixedSizeVariant();
  }

  async setProductFromMouldingShopify () {
    const mouldingOptions = await this.getMouldingOptions();
    const currentMouldingOption = mouldingOptions.mouldings
      .filter( mouldingOption => mouldingOption.shopifyProductId )
      .find( mouldingOption => mouldingOption.permalink == this.moulding.permalink );
    if (currentMouldingOption) {
      if (this.catalog.shopifyProductId != currentMouldingOption.shopifyProductId) {
        this.catalog.shopifyProductId = currentMouldingOption.shopifyProductId;
        await this.loadShopifyProduct();
      }
    } else {
      console.error("Matching moulding not found!");
    }
  }

  // based on: moulding, mat, accent mat, size
  setVariantToClosestVariantShopify () {
    let possibleVariants = this.product.variants.filter(v => v.frameSpec);;

    const mouldingVariants = possibleVariants.filter( v => v.frameSpec.moulding.permalink === this.moulding.permalink );
    if (mouldingVariants.length) {
      possibleVariants = mouldingVariants;
    }

    const matVariants = possibleVariants.filter(
      v => v.frameSpec.mats[0] && this.mats[0] && (v.frameSpec.mats[0].name === this.mats[0].name)
    );
    if (matVariants.length) {
      possibleVariants = matVariants;
    }

    const accentMatVariants = possibleVariants.filter(
      v => v.frameSpec.mats[1] && this.mats[1] && (v.frameSpec.mats[1].name === this.mats[1].name)
    );
    if (accentMatVariants.length) {
      possibleVariants = accentMatVariants;
    }

    // Select on exact size for curated
    if (this.isCuratedDesignFrame()) {
      const sizeVariants = possibleVariants.filter(
        v => v.frameSpec.artworks[0]
          && this.artworks[0]
          && this.artworks[0].exterior
          && (v.frameSpec.artworks[0].exterior.widthInInches === this.artworks[0].exterior.widthInInches)
          && (v.frameSpec.artworks[0].exterior.heightInInches === this.artworks[0].exterior.heightInInches)
      );
      if (sizeVariants.length) {
        possibleVariants = sizeVariants;
      }
    } else {
      // TODO: Select on size group for custom
    }

    this.variant = possibleVariants[0];
    this.catalog.shopifyVariantId = possibleVariants[0].shopifyVariantId;
  }

  // Look into for art size locking after update
  async enforceFixedSizeVariantShopify () {
    const artwork = this.getArtwork();
    // Use template artwork size and auto-crop if necessary
    if (artwork) {
      const oldExteriorRatio = artwork.exterior.widthInInches / artwork.exterior.heightInInches;
      const variantExterior = this.variant.frameSpec.artworks[0].exterior;

      const nextExterior = {
        widthInInches: parseFloat(variantExterior.widthInInches),
        heightInInches: parseFloat(variantExterior.heightInInches)
      };

      if (variantExterior.depthInInches !== undefined && variantExterior.depthInInches !== null) {
        nextExterior.depthInInches = parseFloat(variantExterior.depthInInches);
      } else if (artwork.exterior.depthInInches !== undefined) {
        nextExterior.depthInInches = artwork.exterior.depthInInches;
      }

      artwork.exterior = nextExterior;

      if (!artwork.productCategory && this.isDigital()) {
        artwork.productCategory = "digital_photo";
      }

      const newExteriorRatio = artwork.exterior.widthInInches / artwork.exterior.heightInInches;

      if (newExteriorRatio !== oldExteriorRatio) {
        await this.autoCrop();
      }
    }

    // Save the mat caption and moulding caption
    const matCaption = this.mats[0] ? this.mats[0].matCaption : false;
    const mouldingPlate = this.moulding.mouldingPlate
    const mouldingEngraving = this.moulding.mouldingEngraving;

    await this.setMoulding({permalink: this.variant.frameSpec.moulding.permalink});
    this.adornments = this.variant.adornments;
    this.mount = this.variant.frameSpec.mount;
    this.mats = this.variant.frameSpec.mats;

    // Slice back in the possibly-customized mat caption
    if (this.mats[0]) {
      this.setMatCaption(matCaption);
    }

    // Slice back in the mouldingPlate
    this.moulding.mouldingPlate = mouldingPlate;

    if (mouldingEngraving) {
      this.moulding.mouldingEngraving = mouldingEngraving;
    }
  }

  setVariantFromSizeGroupAndConveyance () {
    let possibleVariants = [...this.product.variants];
    const sizeGroup = this.sizeGroup() && this.sizeGroup().name;
    const sizeGroupVariants = possibleVariants.filter( v => v.selectedOptions[0].value === sizeGroup );
    if (sizeGroupVariants.length) {
      possibleVariants = sizeGroupVariants;
    }

    const conveyance = this.isPhysical() ? "Physical" : "Digital";
    const conveyanceVariants = possibleVariants.filter( v => v.selectedOptions[1].value === conveyance );
    if (conveyanceVariants.length) {
      possibleVariants = conveyanceVariants;
    }
    this.variant = possibleVariants[0];
    this.catalog.shopifyVariantId = possibleVariants[0].shopifyVariantId;
  }

  restoreMountSelections(currentMount) {
   if (currentMount) {
      this.mount = currentMount;
    }
  }

  async updateVariantAndConstraints () {
    const originalVariantId = this.catalog.shopifyVariantId; // Track original variant ID

    // Save current mount settings before updating variant
    const currentMount = this.mount ? { ...this.mount } : null;

    if (this.designersChoiceRequested) {
      this.setVariantFromSizeGroupAndConveyance();
    } else if (this.isCustomProduct()) {
      await this.setProductFromMouldingShopify();
      this.setVariantFromSizeGroupAndConveyance();
    } else if (this.isCuratedDesignFrame()) {
      this.setVariantToClosestVariantShopify();
      await this.enforceFixedSizeVariantShopify();

      if (this.isCuratedLayoutJerseyProduct() || this.isCuratedLayoutGolfBallProduct()) {
        this.restoreMountSelections(currentMount);
      }
    } else if (this.isGetThisLookProduct()) {
      this.catalog.shopifyGetThisLookVariantId = this.catalog.shopifyVariantId;
      this.catalog.shopifyGetThisLookProductId = this.catalog.shopifyProductId;
      await this.setProductFromMouldingShopify();
    } else {
      throw new Error("Unknown frame spec type");
    }

    // Auto-calculate price if variant was updated (like with curatedVariantId)
    if (originalVariantId && this.catalog.shopifyVariantId !== originalVariantId) {
      await this.calculatePrice();
    }
  }

  async enforceFixedSizeVariant () {
    const initialMatCaption = this.mats[0] ? this.mats[0].matCaption : false;

    this.moulding.permalink = this.variant.moulding.name;

    const oldExteriorRatio =
      this.artworks[0].exterior.widthInInches / this.artworks[0].exterior.heightInInches;

    this.artworks[0].exterior = {
      widthInInches: parseFloat(this.variant.fixed_art_width.name),
      heightInInches: parseFloat(this.variant.fixed_art_height.name)
    };

    // TODO: Fixed size does not guarantee digital and we should get rid of this magic value
    this.artworks[0].productCategory = "digital_photo";
    const newExteriorRatio =
      this.artworks[0].exterior.widthInInches / this.artworks[0].exterior.heightInInches;

    if (newExteriorRatio !== oldExteriorRatio) {
      await this.autoCrop();
    }

    if (this.variant.mat.name === noMatName ||
        this.variant.mat.name === acrylicMatName) {
      this.mats = [];
    } else {
      this.mats = snackCaseToCamelCase(this.variant.frame_spec.mats);
      if (initialMatCaption) {
        this.setMatCaption(initialMatCaption);
      }
    }
    this.adornments = snackCaseToCamelCase(this.variant.frame_spec.adornments);

    this.mount = snackCaseToCamelCase(this.variant.frame_spec.mount);
  }

  async autoCrop (args) {
    const saveToServer = args?.saveToServer || false;
    let artworkEditor;

    // check if we need to apply a cropping mask unless it is explicitly excluded
    const croppingMask = this.variant && this.variant.croppingMask;
    const excludeFromPrintedArt = croppingMask && croppingMask.excludeFromPrintedArt;
    const maskImageUrl = excludeFromPrintedArt ? null : this.croppingMaskUrl();

    // if there are no edits, and we have a mask to apply, we need to apply it
    const hasEdits = this.getArtwork() && this.getArtwork().edits;
    const needsMaskApplied = maskImageUrl && !hasEdits;
    
    // bail if we don't need to crop and we don't need to apply a mask
    if (!this.needsCrop() && !needsMaskApplied) return;

    if (maskImageUrl) {
      artworkEditor = await ArtworkEditor.create({
        glazeClient: this.joineryClient,
        frameSpec: this,
        maskImageUrl
      });
      artworkEditor.cropToExterior();
      await artworkEditor.applyEdits(true);
    } else {
      artworkEditor = await ArtworkEditor.create({
        glazeClient: this.joineryClient,
        frameSpec: this
      });
      artworkEditor.cropToExterior();
      await artworkEditor.applyEdits();
    }

    if (saveToServer) {
      await artworkEditor.saveToServer();
    }

    this.artworkEditsApplied = true; // So we know to upload the edited image later
    
    return artworkEditor.editedImageDimensions;
  }
  

  setArtwork (artwork, index = 0) {
    const initialArtwork = {...this.artworks[index]};
    this.artworks[index] = artwork;
    if (artwork) {
      this.setArtworkOpeningDimensions();
    }
    if (this.artworks && this.artworks[index] && this.isCuratedDesignFrame()) {
      this.artworks[index].exterior = initialArtwork.exterior;
    }
  }

  setArtworkOpeningDimensions () {
    if (!this.artworks) {
      return;
    }


    this.artworks.forEach((artwork) => {
      if (artwork.exterior) {
        artwork.exterior.artworkOpeningWidthInInches = artwork.exterior.widthInInches;

        if ((!this.isPhysical() || this.inBundle) && this.mats.length > 0) {
          artwork.exterior.artworkOpeningWidthInInches -= uploadedArtworkMatOverlap;
        }

        artwork.exterior.artworkOpeningHeightInInches = artwork.exterior.heightInInches;

        if ((!this.isPhysical() || this.inBundle) && this.mats.length > 0) {
          artwork.exterior.artworkOpeningHeightInInches -= uploadedArtworkMatOverlap;
        }
      }
    });
  }

  setArtworkDepth (depth) {
    if (!this.artworks[0]) {
      return;
    }

    this.artworks[0].exterior.depthInInches = depth;
  }

  setArtworkItemQuantity (quantity) {
    if (!this.artworks[0]) {
      return;
    }

    this.artworks[0].itemQuantity = quantity;
  }

  getArtworkItemQuantity () {
    if (!this.artworks[0]) {
      return;
    }
    return this.artworks[0].itemQuantity || 1;
  }

  setProductCategorySubtype (productCategorySubtype) {
    if (!this.artworks[0]) {
      return;
    }

    this.artworks[0].productCategorySubtype = productCategorySubtype;
  }

  shouldHaveMatOverlappingArtwork () {
    return (!this.isPhysical() || this.inBundle) && this.mats.length > 0;
  }

  clearArtworkOpeningDimensions () {
    if (this.artworks[0]) {
      this.artworks[0].exterior.artworkOpeningWidthInInches = undefined;
      this.artworks[0].exterior.artworkOpeningHeightInInches = undefined;
    }
  }

  syncArtworkOpeningDimensionsToArtwork () {
    if (this.artworks[0]) {
      this.artworks[0].exterior.artworkOpeningWidthInInches = this.artworks[0].exterior.widthInInches;
      this.artworks[0].exterior.artworkOpeningHeightInInches = this.artworks[0].exterior.heightInInches;
    }
  }

  handleArtworkOpeningDimensions () {
    if (this.artworks[0] && this.isDigital() && this.hasMat()) {
      this.clearArtworkOpeningDimensions();
    } else {
      this.syncArtworkOpeningDimensionsToArtwork();
    }
  }

  setArtworkWidth (value) {
    if (!this.artworks[0]) {
      return;
    }

    this.artworks[0].exterior.widthInInches = value;
    this.setArtworkOpeningDimensions();
  }

  setArtworkHeight (value) {
    if (!this.artworks[0]) {
      return;
    }

    this.artworks[0].exterior.heightInInches = value;
    this.setArtworkOpeningDimensions();
  }

  async setMoulding (moulding) {
    if (!moulding) {
      return;
    }
    const existingBrassPlate = this.moulding && this.moulding.mouldingPlate;
    const existingMouldingEngraving = this.moulding && this.moulding.mouldingEngraving;

    this.moulding = {
      permalink: moulding.permalink,
      mouldingPlateAllowed: moulding.mouldingPlateAllowed
    };
    // If we had a brass plate and the new moulding allows it, add it back
    const mouldingPlateAllowed = await this.mouldingPlateAllowed()
    if (existingBrassPlate && mouldingPlateAllowed) {
      this.setMouldingPlate(existingBrassPlate);
    }

    const mouldingEngravingAllowed = await this.allowsMouldingEngraving();
    if (existingMouldingEngraving && mouldingEngravingAllowed) {
      this.moulding.mouldingEngraving = existingMouldingEngraving;
    }
  }

  async changeToCanvas () {
    await this.setMoulding({ permalink: "heathrow-black-canvas-frame" });
    this.removeMats();
    if (this.artworks[0]) {
      this.artworks[0].medium = "canvas";
    }
  }

  async changeFromCanvas () {
    await this.setMoulding({ permalink: "mercer-slim-black-frame" });
    const mat = { name: whiteMatName };
    this.setMat(mat)
  }

  environment () {
    if (this.joineryHost.includes("docker.internal")) {
      return "development";
    } else if (this.joineryHost.includes("joinery")) {
      return "production";
    } else {
      return "staging";
    }
  }

  convexBaseUrl () {
    if (this.environment() === "development") {
      return "http://host.docker.internal:5001";
    } else if (this.environment() === "staging") {
      return "https://convex.staging.framebridge.io";
    } else {
      return "https://convex.framebridge.io";
    }
  }

  async retrieveProductById (productId) {
    try {
      const resp = await axios.get(`${this.joineryHost}/api/v1/shopify_product_details/${productId}`);
      return resp.data;
    } catch (error) {
      console.error("Error when fetching product", error);
      return null;
    }
  }

  getJerseyProductId () {
    return this.environment() === "production" ? "9050290422075" : "8882862522658";
  }
  
  async retrieveJerseyProduct () {
    const jerseyProductId = this.getJerseyProductId();

    if (!jerseyProductId) {
      return null;
    }

    return await this.retrieveProductById(jerseyProductId);
  }

  getRetailJerseyProductId () {
    return this.environment() === "production" ? "10157044531515" : "9818974290210";
  }

  getRetailGolfBallProductId () {
    return this.environment() === "production" ? "10334037180731" : "10073548325154";
  }

  async retrieveRetailJerseyProduct () {
    const retailJerseyProductId = this.getRetailJerseyProductId();

    if (!retailJerseyProductId) {
      return null;
    }

    return await this.retrieveProductById(retailJerseyProductId);
  }

  getCroppedJerseyProductId () {
    return this.environment() === "production" ? "10157044728123" : "9771436671266";
  }

  async retrieveCroppedJerseyProduct () {
    const croppedJerseyProductId = this.getCroppedJerseyProductId();

    if (!croppedJerseyProductId) {
      return null;
    }

    return await this.retrieveProductById(croppedJerseyProductId);
  }

  setMat (value, customReveal) {
    const fakeMatSkus = [noMatName, canvasMatName, floatMountMatName, acrylicMatName, jerseyMatName, accentMatName];

    if (!fakeMatSkus.includes(value.name)) {
      let reveal;

      if (!this.isCustomProduct() && this.variant?.framing_specification) {
        reveal = {
          topInInches: parseFloat(this.variant.framing_specification.mat_width_in_inches_top),
          rightInInches: parseFloat(this.variant.framing_specification.mat_width_in_inches_right),
          bottomInInches: parseFloat(this.variant.framing_specification.mat_width_in_inches_bottom),
          leftInInches: parseFloat(this.variant.framing_specification.mat_width_in_inches_left)
        };
      } else if (customReveal) {
        reveal = {
          topInInches:  customReveal.topInInches,
          rightInInches:  customReveal.rightInInches,
          bottomInInches:   customReveal.bottomInInches,
          leftInInches:   customReveal.leftInInches
        };
      } else if (this.mats[0] && Boolean(this.mats[0].reveal)) {
        reveal = this.mats[0].reveal;
      } else {
        reveal = {
          topInInches:  this.sizeGroup() ? this.sizeGroup().matWidth : undefined,
          rightInInches:  this.sizeGroup() ? this.sizeGroup().matWidth : undefined,
          bottomInInches:   this.sizeGroup() ? this.sizeGroup().matWidth : undefined,
          leftInInches:   this.sizeGroup() ? this.sizeGroup().matWidth : undefined
        };
      }

      this.mats[0] = {
        name: value.name,
        reveal,
        matCaption: this.mats[0] ? this.mats[0].matCaption : undefined,
        matStyle: value.style ? value.style : undefined
      };
    }

    this.setArtworkOpeningDimensions();
  }

  removeMats () {
    this.mats = [];
    this.setArtworkOpeningDimensions();
  }

  setAccentMat (value) {
    if (!value) {
      value = this.mats[1] ? this.mats[1] : this.product.accent_mat_options[0];
    }

    const accentMatReveal = {
      topInInches: defaultAccentMatReveal,
      rightInInches: defaultAccentMatReveal,
      bottomInInches: defaultAccentMatReveal,
      leftInInches: defaultAccentMatReveal
    };

    const accentMat = {
      name: value.name,
      reveal: accentMatReveal
    };

    this.mats[1] = accentMat;
    this.setArtworkOpeningDimensions();
  }

  removeAccentMats () {
    if (this.mats.length) {
      this.mats.length = 1;
    }

    this.setArtworkOpeningDimensions();
  }

  conformMatRevealToSizeGroup () {
    if (!this.hasMat() || !this.sizeGroup() || !this.isCustomProduct() || this.isPostPurchase() || this.finalFrameSize?.locked) {
      return false;
    }

    const currentMatWidth = this.mats[0]?.reveal?.topInInches;
    const expectedSizeGroup = sizeGroups.find(sg => sg.name === this.sizeGroup().name);
    const expectedMatWidth = expectedSizeGroup?.matWidth;

    if (expectedMatWidth !== undefined && currentMatWidth !== expectedMatWidth) {
      const reveal = {
        topInInches: expectedMatWidth,
        rightInInches: expectedMatWidth,
        bottomInInches: expectedMatWidth,
        leftInInches: expectedMatWidth
      };
      this.setReveal(reveal);
      return true;
    }

    return false;
  }

  setReveal (value, index = 0) {
    const mat = this.mats[index];
    if (!mat) {
      return;
    }

    mat.reveal = {
      topInInches: value.topInInches,
      rightInInches: value.rightInInches,
      bottomInInches: value.bottomInInches,
      leftInInches: value.leftInInches
    };
  }

  setMountReveal (value) {
    if (!this.mount) {
      return;
    }

    this.mount.reveal = {
      topInInches: value.topInInches,
      rightInInches: value.rightInInches,
      bottomInInches: value.bottomInInches,
      leftInInches: value.leftInInches
    };

    this.setArtworkOpeningDimensions();
  }

  getVariantMountReveal () {
    const legacySpec = this.variant?.framing_specification;
    if (legacySpec && legacySpec.artwork_mount_reveal_in_inches_top != null) {
      return {
        topInInches: parseFloat(legacySpec.artwork_mount_reveal_in_inches_top),
        rightInInches: parseFloat(legacySpec.artwork_mount_reveal_in_inches_right),
        bottomInInches: parseFloat(legacySpec.artwork_mount_reveal_in_inches_bottom),
        leftInInches: parseFloat(legacySpec.artwork_mount_reveal_in_inches_left)
      };
    }

    const shopifyReveal = this.variant?.frameSpec?.mount?.reveal;
    if (shopifyReveal) {
      return {
        topInInches: parseFloat(shopifyReveal.topInInches),
        rightInInches: parseFloat(shopifyReveal.rightInInches),
        bottomInInches: parseFloat(shopifyReveal.bottomInInches),
        leftInInches: parseFloat(shopifyReveal.leftInInches)
      };
    }

    return null;
  }

  getVariantMountElevated () {
    const shopifyMount = this.variant?.frameSpec?.mount;
    if (shopifyMount && typeof shopifyMount.elevated === "boolean") {
      return shopifyMount.elevated;
    }

    return false;
  }

  revealForWidth (width) {
    return {
      topInInches: width,
      rightInInches: width,
      bottomInInches: width,
      leftInInches: width
    };
  }

  resolveMountElevation ({ name, elevated }) {
    if (typeof elevated === "boolean") {
      return elevated;
    }

    if (name === floatMountMatName || name === acrylicMatName) {
      return true;
    }

    return false;
  }

  resolveMountMatSku ({ name, sku }) {
    if (sku) {
      return sku;
    }

    if (name === floatMountMatName) {
      return whiteMatName;
    }

    return name || whiteMatName;
  }

  resolveMountReveal ({ reveal }, elevated, wasFloated, isCustom) {
    if (reveal) {
      return reveal;
    }

    if (!elevated) {
      return this.revealForWidth(0);
    }

    if (isCustom && wasFloated) {
      return this.mount.reveal;
    }

    return this.revealForWidth(floatMountMatReveal);
  }

  setMount (mount) {
    mount = mount || {};
    const wasFloated = this.isFloated();
    const elevated = this.resolveMountElevation(mount);
    const isCustom = this.isCustomProduct();
    const variantReveal = isCustom ? null : this.getVariantMountReveal();

    if (variantReveal) {
      this.mount.reveal = variantReveal;
      this.mount.elevated = this.getVariantMountElevated();
    } else {
      this.mount.reveal = this.resolveMountReveal(mount, elevated, wasFloated, isCustom);
      this.mount.elevated = elevated;

      if (isCustom || elevated) {
        const matSku = this.resolveMountMatSku(mount);
        this.mount.name = matSku;
        this.mount.sku = matSku;
      }
    }

    this.setArtworkOpeningDimensions();
  }

  setMountBaseMat (mat) {
    this.mount.name = mat.name;
    this.mount.sku = mat.name;
  }

  snapMountToVariant () {
    if (!this.mount) {
      return;
    }

    const variantReveal = this.getVariantMountReveal();
    if (variantReveal) {
      this.mount.reveal = variantReveal;
      this.mount.elevated = this.getVariantMountElevated();
    } else {
      this.mount.reveal = this.revealForWidth(0);
      this.mount.elevated = false;
    }

    this.setArtworkOpeningDimensions();
  }

  applyMountWidth (reveal) {
    if (!this.isCustomProduct()) {
      this.snapMountToVariant();
      return;
    }

    this.setMountReveal(reveal);
  }

  applyMountBaseMat (mat) {
    if (this.isCustomProduct()) {
      this.setMountBaseMat(mat);
      return;
    }

    const elevated = this.mount?.elevated;
    this.snapMountToVariant();
    this.setMountBaseMat(mat);
    if (this.mount) {
      this.mount.elevated = elevated;
    }
  }

  setElevatedMountByMatSku (matSku) {
    this.mount.reveal = this.revealForWidth(floatMountMatReveal);
    this.mount.elevated = true;
    this.mount.name = matSku;
    this.mount.sku = matSku;
  }

  setKraftPaper (kraftPaper) {
    this.customKraftPaper = kraftPaper;
  }

  removeKraftPaper () {
    delete this.customKraftPaper;
  }

	setMatPlaque (matPlaque) {
		this.matPlaque = matPlaque;
	}

	removeMatPlaque () {
		delete this.matPlaque;
	}

  isKraftPaperAvailable () {
    const disabledFor = ["piccolo", "mezzo", "regalo"];
    const isDisabled = disabledFor.find(
      (frame) => this.moulding.permalink.includes(frame)
    ) || this.isOrnamentFrame();

    return this.product && this.product.tags ? (
      this.product.tags.includes("custom-kraft-paper")
      || this.product.tags.includes("custom-kraft-paper-auto")
    ) : !isDisabled;
  }
  isAutoKraftPaper () {
    return this.product && this.product.tags && this.product.tags.includes("custom-kraft-paper-auto");
  }

  isAutoGiftBox () {
    return this.product && this.product.tags && this.product.tags.includes("gift-box-auto");
  }

  isAutoMatPlaque () {
    return this.product && this.product.tags && this.product.tags.includes("mat-plaque-auto");
  }

  setMatCaption (matCaption) {
    if (this.mats[0] && matCaption) {
      const scratchMatCaption = {...matCaption};

      const stylesDefinedPerLine = Boolean(matCaption.lines && Object.values(matCaption.lines).some((line) => line.fontStyle));

      if (stylesDefinedPerLine) {
        const lines = matCaption && matCaption.lines ? matCaption.lines : {};
        scratchMatCaption.layoutName = matCaption.layoutName;
        scratchMatCaption.lines = {
          line1: {
            fontAlignment: matCaption.fontAlignment,
            fontStyle: lines.line1 && lines.line1.fontStyle ? lines.line1.fontStyle : undefined,
            fontSize: lines.line1 && lines.line1.fontSize ? lines.line1.fontSize : undefined,
            message: lines.line1 && lines.line1.message ? lines.line1.message : undefined,
          },
          line2: {
            fontAlignment: matCaption.fontAlignment,
            fontStyle: lines.line2 && lines.line2.fontStyle ? lines.line2.fontStyle : undefined,
            fontSize: lines.line2 && lines.line2.fontSize ? lines.line2.fontSize : undefined,
            message: lines.line2 && lines.line2.message ? lines.line2.message : undefined,
          },
          line3: {
            fontAlignment: matCaption.fontAlignment,
            fontStyle: lines.line3 && lines.line3.fontStyle ? lines.line3.fontStyle : undefined,
            fontSize: lines.line3 && lines.line3.fontSize ? lines.line3.fontSize : undefined,
            message: lines.line3 && lines.line3.message ? lines.line3.message : undefined,
          },
          line4: {
            fontAlignment: matCaption.fontAlignment,
            fontStyle: lines.line4 && lines.line4.fontStyle ? lines.line4.fontStyle : undefined,
            fontSize: lines.line4 && lines.line4.fontSize ? lines.line4.fontSize : undefined,
            message: lines.line4 && lines.line4.message ? lines.line4.message : undefined,
          },
        }
      }

      this.mats[0].matCaption = scratchMatCaption;
    }
  }

  removeMatCaption () {
    if (this.mats[0]) {
      delete this.mats[0].matCaption;
    }
  }

  hasMatCaption () {
    return Boolean(this.mats
      && this.mats[0]
      && this.mats[0].matCaption
    );
  }

  setStoryPocket (storyPocket) {
    this.storyPocket = storyPocket;
  }

  removeStoryPocket () {
    delete this.storyPocket;
  }

  setHangingHardwareToDefault() {
    delete this.hangingHardware;
  }

  setMiniStoryPocket (miniStoryPocket) {
    this.miniStoryPocket = miniStoryPocket;
  }

  removeMiniStoryPocket () {
    delete this.miniStoryPocket;
  }

  setCustomKraftPaper (customKraftPaper) {
    this.customKraftPaper = customKraftPaper;
  }

  removeCustomKraftPaper () {
    delete this.customKraftPaper;
  }

  setMouldingPlate (mouldingPlate) {
    if (!this.moulding) {
      return;
    }

    this.moulding.mouldingPlate = mouldingPlate;
  }

  removeMouldingPlate () {
    if (!this.moulding) {
      return;
    }

    delete this.moulding.mouldingPlate;
  }

  hasMouldingPlate () {
    return Boolean(
      this.moulding
      && this.moulding.mouldingPlate
      && (
        this.moulding.mouldingPlate.line1
        || this.moulding.mouldingPlate.line2
      )
    );
  }

  setGiftBox (giftBoxSku) {
    this.giftBox = { sku: giftBoxSku };
  }

  removeGiftBox (config = {
    soft: false
  }) {
    if (config.soft) {
      delete this.giftBox;
    } else {
      this.giftBox = { explicitlyRemoved: true };
    }
  }

  setMatPlaque (matPlaque) {
    this.matPlaque = matPlaque;
  }

  removeMatPlaque () {
    delete this.matPlaque;
  }

  parseMatPlaqueOptions (selectedOptions) {
    const colorOption = selectedOptions.find(option => option.name === 'Plaque Color');
    const layoutOption = selectedOptions.find(option => option.name === 'Layout');
    const frameTypeOption = selectedOptions.find(option => option.name === 'Frame Type');

    return {
      color: colorOption ? colorOption.value : undefined,
      layout: layoutOption ? layoutOption.value : undefined,
      frameType: frameTypeOption ? frameTypeOption.value : undefined
    };
  }

  isMatPlaqueEmpty () {
    if (!this.matPlaque) {
      return true;
    }

    if (this.matPlaque.templateLayout === 'Personalized Layout') {
      return false;
    }

    const headerLine1 = this.matPlaque.header && this.matPlaque.header.line1;
    const headerLine2 = this.matPlaque.header && this.matPlaque.header.line2;
    const subheaderLine1 = this.matPlaque.subheader && this.matPlaque.subheader.line1;
    const subheaderLine2 = this.matPlaque.subheader && this.matPlaque.subheader.line2;
    const bodyCopyText = this.matPlaque.bodyCopy && this.matPlaque.bodyCopy.text;
    const bodyCopy = this.matPlaque.body && this.matPlaque.body.copy;
    const signatureLine = this.matPlaque.signature && this.matPlaque.signature.line;

    return !(
      headerLine1 || headerLine2 ||
      subheaderLine1 || subheaderLine2 ||
      bodyCopyText || bodyCopy ||
      signatureLine
    );
  }

  setMouldingEngraving (mouldingEngraving) {
    this.moulding.mouldingEngraving = mouldingEngraving;
  }

  removeMouldingEngraving () {
    delete this.moulding.mouldingEngraving;
  }

  isMouldingEngravingEmpty () {
    if (!this.moulding?.mouldingEngraving) {
      return true;
    }

    return !(
      this.moulding.mouldingEngraving.line1 ||
      this.moulding.mouldingEngraving.line2 ||
      this.moulding.mouldingEngraving.line3
    );
  }

  hasMouldingEngraving () {
    return Boolean(
      this.moulding && this.moulding.mouldingEngraving
    );
  }

  hasPersonalizationNotAvailableInStore () {
    // Check for moulding engraving, plate, mat caption, or plaque.
    // Don't exclude mini story pockets as those come with ornaments and can be written in store.
    return Boolean(this.hasMouldingPlate() || this.hasMouldingEngraving() || this.hasMatCaption() || this.matPlaque || this.storyPocket);
  }

  setAcrylic () {
    const skuToSet = this.isOversized() ? '13-023' : '13-022';
    this.customAcrylic = { sku: skuToSet };
    this.removeNoAcrylic();
  }

  removeAcrylic () {
    delete this.customAcrylic;
  }

  setNoAcrylic () {
    this.acrylicRequired = false;
    this.removeAcrylic();
  }

  removeNoAcrylic () {
    this.acrylicRequired = true;
  }

  noAcrylicDisabled () {
    return this.mats.length || this.mount.elevated;
  }

  setHardware (hardwareValue) {
    this.hangingHardware = hardwareValue;
  }

  removeHardware () {
    delete this.hangingHardware;
  }

  async setDesignersChoiceRequested() {
    this.designersChoiceRequested = true;

    // TODO: Look up by slug instead of hard-wiring the ID
    const envDesignerChoiceId = this.shopifyHost == "https://staging.framebridge.dev" ? "8238233420066" : "8671385878843";
    this.catalog.shopifyProductId = envDesignerChoiceId;

    if (this.productCategory() === "canvas") {
      await this.changeToCanvas();
    }

    await this.loadShopifyProduct();
  }

  async setDesignersChoiceCustomerPreferences(content) {
    this.designersChoiceCustomerPreferences = content;
  }

  rebuildAssociateInstructionTags () {
    if (!this.associateInstructions || !this.associateInstructions.instructions) {
      return;
    }

    // Instructions that should add tags when answered "Yes"
    const yesInstructions = [
      "Double Sided Art",
      "Encapsulation",
      "Mat Reveal / Spacing",
      "Textile Tag",
      "Clear Float Corner",
      "Permission to Trim"
    ];

    // Instructions that should add tags when answered "No"
    const noInstructions = [
      "Multi Layout",
      "Custom Jersey Layout"
    ];

    const tags = new Set();

    this.associateInstructions.instructions.forEach((instruction) => {
      if (!instruction.tag) return;

      const wasOptionallySet = !instruction.primaryQuestion.required;

      // Check if this instruction should add its tag based on its response
      const shouldAddTag = wasOptionallySet ||
        (instruction.primaryQuestion.response === true && yesInstructions.includes(instruction.name)) ||
        (instruction.primaryQuestion.response === false && noInstructions.includes(instruction.name));

      if (shouldAddTag) {
        tags.add(instruction.tag);
      }
    });

    this.associateInstructions.tags = Array.from(tags);
  }

  setAssociateInstruction (instruction) {
    if (!this.associateInstructions) {
      this.associateInstructions = {};
    }
    if (!this.associateInstructions.instructions) {
      this.associateInstructions.instructions = [];
    }
    if (!this.associateInstructions.tags) {
      this.associateInstructions.tags = [];
    }

    // Find existing instruction with the same name
    const existingIndex = this.associateInstructions.instructions.findIndex(
      (existingInstruction) => existingInstruction.name === instruction.name
    );

    if (existingIndex !== -1) {
      // Replace existing instruction
      this.associateInstructions.instructions[existingIndex] = instruction;
    } else {
      // Add new instruction
      this.associateInstructions.instructions.push(instruction);
    }

    // Rebuild tags based on all instructions
    this.rebuildAssociateInstructionTags();
  }

  removeAssociateInstruction (instructionName) {
    if (!this.associateInstructions || !this.associateInstructions.instructions) {
      return;
    }

    // Remove instruction by name
    this.associateInstructions.instructions = this.associateInstructions.instructions.filter(
      (instruction) => instruction.name !== instructionName
    );

    // Rebuild tags based on remaining instructions
    this.rebuildAssociateInstructionTags();
  }

  getFromLocalStorage () {
    // While it is true that we store the user's frameSpec in localstorage, this is a practice that
    // we try to avoid.
    if (typeof window === "undefined") {
      return;
    }

    const stringyFrameSpec = window.localStorage.getItem("currentFrameSpec");

    if (stringyFrameSpec) {
      return JSON.parse(stringyFrameSpec);
    }
  };

  setInLocalStorage () {
    // While it is true that we store the user's frameSpec in localstorage, this is a practice that
    // we try to avoid.
    if (typeof window === "undefined") {
      return;
    }

    window.localStorage.setItem("currentFrameSpec", JSON.stringify(this));

    return window.localStorage.getItem("currentFrameSpec");
  };

  clearFromLocalStorage () {
    // While it is true that we store the user's frameSpec in localstorage, this is a practice that
    // we try to avoid.
    if (typeof window === "undefined") {
      return;
    }

    window.localStorage.removeItem("currentFrameSpec");
    window.localStorage.removeItem("uploadedArtwork"); 
  };

  // FrameSpec Setters and Mutations ↑
}
